// Performance Radar — frontend controller (Figma UI). Two-phase flow (build-spec §2):
// IMPORT (attach/drop a transcript file) → classify → auto-load template into the editable
// performance table → user review/override → RUN locks + streams the Runtime Monitor +
// renders results. STOP = hard reset.

import * as api from './lib/api.js';
import type {
  ImportResponse,
  ManagerScorecard,
  RunEvent,
  AggregateResult,
  MeddpiccReport,
  BuyerSideResult,
  UnscoredSkill,
} from './lib/types.js';
import { wireFileImport } from './lib/importer.js';
import { displaySkillName } from './lib/skills.js';
import { wireCollapse } from './lib/collapse.js';
import { ControlBoard } from './ui/controlBoard.js';
import { PerformanceTable } from './ui/performanceTable.js';
import { LifecycleJourney, estimateDurationMin } from './ui/lifecycle.js';
import { renderResults, clearResults, renderTrend, renderCrossSkillIdle, type ResultEls } from './ui/results.js';
import { updateTrendTargets } from './ui/trendOverlay.js';
import { initMomentumBanner, maybeShowMomentumBanner, onMomentumViewChange, dismissMomentumBanner, getBannerPref, setBannerPref } from './ui/momentumBanner.js';
import { initCoachingSignal, showCoachingSignal, hideCoachingSignal, onCoachingSignalView } from './ui/coachingSignal.js';
import { runtimeProgress, stopRuntimeProgress } from './ui/runtimeProgress.js';
import { mountScanLab, scanLabPhase, resetScanLab } from './ui/scanLab.js';
import { isTrendsEnabled, setTrendsEnabled, loadTrendsSetting } from './lib/settings.js';
import { renderMeddpicc, clearMeddpicc, renderMeddpiccUnavailable } from './ui/meddpicc.js';
import { renderCoaching, renderCoachingIdle, coachingLoading, skillCoachingDetail } from './ui/coaching.js';
import { renderReport, waitForReportImages, type ReportMeta } from './ui/report.js';
import { controlStates, type Phase } from './ui/controls.js';
import { wireSideNav, navigateTo, onViewChange, applyRoleNav } from './ui/sidenav.js';
import { wireSupport } from './ui/support.js';
import { renderAccount } from './ui/account.js';
import { renderBilling } from './ui/billing.js';
import { renderTeamsPage } from './ui/teams.js';
import { renderTrendsPage, renderHeatmapPage } from './ui/trends.js';
import { renderTeamTrends } from './ui/teamTrends.js';
import { renderHistory, setReportOpener } from './ui/history.js';
import { renderCompare } from './ui/compare.js';
import { ScorecardsPage, ALL_OPTIONAL_WARNING, isAllOptional } from './ui/scorecards.js';
import { renderSecretSauce } from './ui/secretSauce.js';
import { BuyerSidePage } from './ui/buyerSide.js';
import { mountCallBar, callBarVisible, fmtCallTime } from './ui/callBar.js';
import { bonusInfoIcon } from './lib/bonusCopy.js';
import { improvementGaps, missedShortfalls, unplaceableScoredSkills } from './ui/improvement.js';
import { setText, escapeHtml } from './lib/escape.js';
import { BLANK_SCORECARD } from './lib/defaultScorecard.js';
import { saveDiagnostic, loadDiagnostic, clearDiagnostic, saveView, loadView, clearView, purgeLegacyDeviceStorage } from './lib/persist.js';
import { initAppClerk, signOutClerk, onClerkUser, applyAvatar } from './lib/appClerk.js';
import { CLERK_ENABLED } from './lib/clerkConfig.js';

// Keep the Clerk session alive + attach a fresh bearer token to every /api call. Must run before the
// first API call below (getSettings/getMe/etc.). No-op on prod (dead-code-eliminated); dual-auth-safe.
initAppClerk();

const $ = (id: string) => document.getElementById(id)!;

// Access-session-expiry recovery: when an /api call is redirected to the Access login (session timed
// out), show a brief full-screen notice and reload — a full-page navigation re-triggers the Access
// login cleanly, instead of the cryptic "Failed to fetch" a mid-session redirect would otherwise
// Demo mode: sync the ?demo=1 / ?demo=0 flag from the URL once at boot (adds X-Demo to every /api call,
// which the staging worker serves the demo tenant for; prod ignores it).
api.initDemoFlag();
// Staging-only Home toggle so demo mode is one click (no URL editing). Hidden on prod (hostname gate).
(() => {
  const box = document.getElementById('demo-toggle');
  if (!box || !location.hostname.includes('staging')) return;
  const on = api.isDemoOn();
  box.hidden = false;
  box.classList.toggle('is-on', on);
  const stateEl = document.getElementById('demo-toggle-state');
  if (stateEl) stateEl.textContent = on ? 'ON' : 'OFF';
  const btn = document.getElementById('demo-toggle-btn') as HTMLButtonElement | null;
  if (btn) {
    btn.textContent = on ? 'Turn off' : 'Turn on';
    btn.setAttribute('aria-pressed', String(on));
    // Reload WITHOUT the query string: a leftover ?demo=1 in the URL would otherwise be re-applied by
    // initDemoFlag() on the next load and override "Turn off". The localStorage flag alone drives it now.
    btn.addEventListener('click', () => { api.setDemoMode(!on); location.href = location.pathname + location.hash; });
  }
})();

// produce. Latched so concurrent failed calls don't stack notices or double-reload.
let sessionExpiredShown = false;
api.setAuthExpiredHandler(() => {
  if (sessionExpiredShown) return;
  sessionExpiredShown = true;
  const overlay = document.createElement('div');
  overlay.className = 'session-expired-overlay';
  overlay.setAttribute('role', 'alert');
  overlay.textContent = 'Your session expired. Reloading to sign you back in…';
  document.body.appendChild(overlay);
  window.setTimeout(() => window.location.reload(), 1600);
});

const els = {
  importBtn: $('import-btn'),
  runBtn: $('run-btn') as HTMLButtonElement,
  stopBtn: $('stop-btn') as HTMLButtonElement,
  pdfBtn: $('pdf-btn') as HTMLButtonElement,
  emailBtn: $('email-btn') as HTMLButtonElement,
  clearBtn: $('clear-btn') as HTMLButtonElement,
  resetBtn: $('reset-btn') as HTMLButtonElement,
  // Save was removed from this page (scorecards are edited/saved only on the Scorecards page). Kept
  // as an optional lookup so the residual wiring/policy guards degrade to no-ops if it's absent.
  saveBtn: document.getElementById('save-template-btn') as HTMLButtonElement | null,
  fileInput: $('file-input') as HTMLInputElement,
  notif: $('notifications'),
  // Call Details are display-only inline text (system-populated), so they're plain elements.
  repName: $('rep-name'),
  repRole: $('rep-role'),
  businessName: $('business-name'),
  callType: $('call-type'),
  callDuration: $('call-duration'),
};

// The Quick Snapshot page shows its own copy of the Message Center (#sk-notifications) inside the
// collapsible Enablement section. The shared strip's #notifications stays the primary (+ file drop
// zone, unchanged for the other lenses); this observer mirrors every update into the Seller copy so
// the same live progress log — including the typewriter ticks — shows there too.
// Mirror every update into Quick Snapshot's read-only copy (#sk-notifications) so the same live progress
// log shows there too. Looked up live; skipped if absent.
const mirrorNotif = (): void => {
  const box = document.getElementById('sk-notifications');
  if (box) {
    box.innerHTML = els.notif.innerHTML;
    box.scrollTop = box.scrollHeight;
  }
};
new MutationObserver(mirrorNotif).observe(els.notif, { childList: true, subtree: true, characterData: true });
mirrorNotif();

// The Message Center's first-run welcome — shown immediately on load, then personalised with the
// signed-in user's first name once the Cloudflare Access identity resolves. (The AI-use disclosure
// no longer lives here; it's at the top of the Trust Center page, and import is no longer gated.)
function welcomeInner(firstName: string): string {
  const greeting = firstName ? `Welcome ${escapeHtml(firstName)},` : 'Welcome,';
  return `<strong>${greeting}</strong> to get started click <em>Import Transcript</em> or drop a file here. This message box reports progress, warnings, and run status.`;
}
const WELCOME_HTML = `<p class="notif-welcome" id="welcome-msg">${welcomeInner('')}</p>`;

// Shown in the Message Center after Clear — a neutral "ready for the next call" state, not the
// first-run welcome (the user is already mid-session).
const CLEARED_HTML =
  '<p class="notif-welcome"><strong>Cleared.</strong> Ready for the next call, click <em>Import Transcript</em> or drop a file here to score another. This box reports progress, warnings, and run status.</p>';

// Render the welcome now, then swap in the first name (best-effort — the generic welcome stays on any
// failure, e.g. local dev with no Access session).
els.notif.innerHTML = WELCOME_HTML;
void greetUser();

async function greetUser(): Promise<void> {
  try {
    const res = await fetch('/cdn-cgi/access/get-identity', { credentials: 'include' });
    if (!res.ok) return;
    const id = (await res.json()) as { name?: string; email?: string };
    const first = firstNameFrom(id);
    if (!first) return;
    const msg = document.getElementById('welcome-msg');
    if (msg) msg.innerHTML = welcomeInner(first);
  } catch {
    /* keep the generic welcome */
  }
}

// Home hero eyebrow: "Welcome <name>,". Driven by the CLERK identity (the same source that fills the
// account chip), not the Access get-identity endpoint — this app authenticates via Clerk, so that
// endpoint returns nothing in prod and left the (hidden-by-default) line invisible. Hidden until a
// real first name resolves, so no bare "Welcome ," ever flashes.
onClerkUser((info) => {
  const el = document.getElementById('home-welcome');
  if (!el) return;
  const first = (info?.firstName ?? '').trim();
  if (first) {
    el.textContent = `Welcome ${first},`;
    el.hidden = false;
  } else {
    el.hidden = true;
  }
});

/** Best first name from the Access identity: the name claim if present, else the email local part. */
function firstNameFrom(id: { name?: string; email?: string }): string {
  const name = (id.name ?? '').trim();
  if (name) return name.split(/\s+/)[0] ?? '';
  const local = (id.email ?? '').trim().split('@')[0] ?? '';
  const token = local.split(/[._+-]/)[0] ?? '';
  if (token) return token.charAt(0).toUpperCase() + token.slice(1);
  return '';
}

const resultEls: ResultEls = {
  circle: $('score-circle'),
  meaning: $('score-meaning'),
  coverage: $('score-coverage'),
  // Plain getElementById (not $): these are optional, and $ asserts non-null.
  bonusLabel: document.getElementById('score-bonus-label'),
  bonus: document.getElementById('score-bonus'),
  analysis: $('score-analysis'),
  recommendations: $('recommendations'),
  crossSkill: $('cross-skill-content'),
  trend: $('trend-panel'),
};
// Paint the Cross-Skill idle (two-halves + demo web) on load; a restored/scored result overwrites it.
renderCrossSkillIdle(resultEls.crossSkill);

// Publish the trend SVG's scale (renderedWidth / VW, VW=720) as --trend-scale so its text (skill
// labels, legend, axis) renders at its intended px despite the SVG scaling up to fill its column;
// the CSS divides each font size by it.
const trendPanelEl = document.getElementById('trend-panel');
const TREND_VW = 720;
function applyTrendScale(): void {
  const svg = trendPanelEl?.querySelector('.trend-svg') as SVGElement | null;
  const sw = svg?.getBoundingClientRect().width ?? 0;
  if (svg && sw > 10) svg.style.setProperty('--trend-scale', (sw / TREND_VW).toFixed(4));
}
if (trendPanelEl && 'ResizeObserver' in window) new ResizeObserver(applyTrendScale).observe(trendPanelEl);
// The trend SVG is rebuilt on the async series hydrate (and every re-run), which drops the scale var —
// reapply it whenever #trend-panel's children change.
if (trendPanelEl && 'MutationObserver' in window) new MutationObserver(applyTrendScale).observe(trendPanelEl, { childList: true, subtree: true });
// The trend SVG needs its label scale set once it has been laid out, which only happens when its view
// is actually visible. It lives on the Coaching Summary page (moved off Quick Snapshot), so key this
// on 'coaching' — on 'diagnostic' the panel is display:none and every measurement would be zero.
onViewChange((v) => { if (v === 'coaching') applyTrendScale(); }); // first show, once laid out
// On Quick Snapshot, match the cockpit card's bottom to the rail's Rep Classification card once laid out.
onViewChange((v) => { if (v === 'diagnostic') requestAnimationFrame(syncPerfInfoHeight); });
applyTrendScale();

// "Anonymize on import" toggle in the toolbar (optional; drives the server-side in-app scrub).
const anonToggle = document.getElementById('anon-toggle') as HTMLInputElement | null;
const board = new ControlBoard($('control-board'));
const table = new PerformanceTable($('perf-table'));
const lifecycle = new LifecycleJourney($('lifecycle-journey'));

let session: ImportResponse | null = null;
let transcript = '';
// The raw imported file, retained so toggling Anonymize AFTER import can re-classify the same file
// under the new setting (an anonymized import returns SCRUBBED text into `transcript`, so we can't
// recover the raw from there). Cleared on Clear.
let rawTranscript = '';
let importAbort: AbortController | null = null;
let runAbort: AbortController | null = null;
let lastResult: AggregateResult | null = null;
// The not-scored skills (with the scorer's reasons) from the last coaching fetch — used by the pill-hover
// detail below the cockpit so a Missing/unscored skill reads word-for-word the same as the Coaching Summary.
let lastUnscored: UnscoredSkill[] = [];

// --- "Add this call to Benchmark" (verdict band) ---------------------------------------------------
// Per-transcript include/exclude for the cross-call analytics (Trends / Benchmark / Average Scores).
// The choice is remembered in localStorage keyed by a stable transcript fingerprint, so it survives a
// refresh and is restored when the SAME call is imported again. Default = included (checkbox checked).
const benchmarkOpt = document.getElementById('benchmark-opt');
const benchmarkToggle = document.getElementById('benchmark-toggle') as HTMLInputElement | null;
const BENCH_KEY = 'score.benchmark.include';
function benchFingerprint(text: string): string {
  const s = text.trim().replace(/\s+/g, ' ');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; // djb2, non-crypto
  return `${s.length}:${(h >>> 0).toString(36)}`;
}
function benchStore(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(BENCH_KEY) || '{}') as Record<string, boolean>; } catch { return {}; }
}
function benchPrefFor(text: string): boolean {
  if (!text.trim()) return true;
  return benchStore()[benchFingerprint(text)] !== false; // default included
}
function setBenchPref(text: string, include: boolean): void {
  if (!text.trim()) return;
  try {
    const store = benchStore();
    store[benchFingerprint(text)] = include;
    localStorage.setItem(BENCH_KEY, JSON.stringify(store));
  } catch { /* storage unavailable — the server call below still applies the change */ }
}
/** Reflect the stored choice on the checkbox and show the control once a call is scored. */
function syncBenchmarkUI(): void {
  if (!benchmarkOpt || !benchmarkToggle) return;
  if (!transcript.trim() || !lastResult) { benchmarkOpt.hidden = true; return; }
  benchmarkToggle.checked = benchPrefFor(transcript);
  benchmarkOpt.hidden = false;
}
benchmarkToggle?.addEventListener('change', () => {
  if (!transcript.trim()) return;
  const include = benchmarkToggle.checked;
  setBenchPref(transcript, include);
  // Apply immediately to the already-persisted row; best-effort (a not-yet-persisted call no-ops server-side).
  void api.setBenchmarkInclusion(transcript, include).catch(() => {});
});
// A restored Buyer-Signals result is captured here (restore runs before buyerSide is constructed) and
// re-rendered once the BuyerSidePage exists (see the buyerSide wiring below).
let restoredBuyerResult: BuyerSideResult | null = null;
// persistNow() can fire DURING restoreDiagnostic (via applyResult), which runs before `buyerSide` is
// constructed. Reading buyerSide directly there threw (undefined.getResult()), the catch wiped
// storage, and a refresh lost the whole diagnostic. So persist reads the buyer result through this
// indirection: null until BuyerSidePage exists, then wired to it.
let getBuyerResult: () => BuyerSideResult | null = () => null;
// The MEDDPICC scan was previously rendered straight to the DOM and never held, so it could not be
// restored after a refresh. Keep the last report so the snapshot can carry it.
let lastMeddpicc: MeddpiccReport | null = null;
let resetBaseline: ManagerScorecard | null = null; // the applied scorecard; Reset restores this
// Session cache of already-scored results, keyed by the score-affecting scorecard fields (each skill's
// expected minimum + critical flag). Lets Reset restore instantly with zero network: a hit is by
// construction the exact result that scorecard produces, so it can never be stale or wrong. Populated on
// every scored render; missed only when a scorecard was never scored this session (then Reset re-scores).
const scoredCache = new Map<string, AggregateResult>();
function scorecardSig(sc: ManagerScorecard): string {
  return JSON.stringify(
    sc.skills
      .map((s) => [s.skill_id, s.expected_score, s.importance])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  );
}
// Scorecards may only be customized once a transcript is imported AND a call type is identified.
// When false (pre-import, or call type not identified) the table is locked and Save is disabled.
let customizable = false;
// True once a transcript has been imported this session (gates Clear — nothing to clear on a
// pristine load or right after Clear). Set on import, reset on Clear.
let hasContent = false;
let dirty = false; // manager-defined settings differ from the defaults

// Settings → "Enable Trends" (server-backed; ON for new tenants, switchable off). Load the value on start,
// then reflect it on the checkbox and the trend panel; toggling writes through to /api/settings.
const trendsOptin = document.getElementById('trends-optin') as HTMLInputElement | null;
void loadTrendsSetting().then(() => {
  if (trendsOptin) trendsOptin.checked = isTrendsEnabled();
  if (lastResult && resultEls.trend) {
    renderTrend(resultEls.trend, lastResult); // immediate single-call / disclaimer
    // A restored diagnostic ran applyResult() → hydrateTrendSeries() at startup, BEFORE this async
    // settings load resolved — so hydrate saw isTrendsEnabled()===false and bailed, leaving the graph
    // stuck on the single-call fallback. Now that the setting is known, pull the rep's history in so a
    // refreshed page shows the same series as the Coaching Trends panel.
    void hydrateTrendSeries();
  }
});
if (trendsOptin) {
  trendsOptin.addEventListener('change', () => {
    setTrendsEnabled(trendsOptin.checked);
    if (lastResult && resultEls.trend) {
      renderTrend(resultEls.trend, lastResult); // immediate (single call / disclaimer)
      void hydrateTrendSeries(); // then pull the rep's history in
    }
  });
}

// Auto-save reports (Phase 3): mirror the tenant setting into a local flag + the toggle; onDownload
// archives the report when on. Best-effort; failures never affect the download. The whole setting is
// hidden if the R2 binding isn't available (feature off for the deploy).
let autoSaveOn = false;
const autosaveOptin = document.getElementById('autosave-optin') as HTMLInputElement | null;
const autosaveItem = document.getElementById('autosave-item');
// Email recipients (report CC): up to 5 addresses + a toggle; the EMAIL button also sends to them when on.
const recipientsOptin = document.getElementById('recipients-optin') as HTMLInputElement | null;
const recipientInputs = Array.from(document.querySelectorAll<HTMLInputElement>('#recipients-list .recipient-input'));
const recipientsMsg = document.getElementById('recipients-msg');
void api
  .getSettings()
  .then((s) => {
    autoSaveOn = s.auto_save_pdf;
    if (autosaveOptin) autosaveOptin.checked = s.auto_save_pdf;
    if (autosaveItem && !s.auto_save_available) autosaveItem.hidden = true;
    if (recipientsOptin) recipientsOptin.checked = Boolean(s.report_cc_enabled);
    (s.report_recipients ?? []).forEach((addr, i) => {
      if (recipientInputs[i]) recipientInputs[i].value = addr;
    });
  })
  .catch(() => undefined);
if (autosaveOptin) {
  autosaveOptin.addEventListener('change', () => {
    autoSaveOn = autosaveOptin.checked;
    void api.setAutoSavePdf(autosaveOptin.checked).catch(() => undefined);
  });
}
// Auto-save: each recipient field saves on blur/Enter (like the other preferences); the toggle saves on
// change. An invalid, non-empty field is highlighted and holds off the save so a typo can't wipe the saved
// list. A brief status flashes, then clears.
let recipientsMsgTimer: ReturnType<typeof setTimeout> | undefined;
function flashRecipientsMsg(kind: 'ok' | 'err', text: string): void {
  if (!recipientsMsg) return;
  recipientsMsg.className = 'recipients-msg ' + kind;
  recipientsMsg.textContent = text;
  if (recipientsMsgTimer) clearTimeout(recipientsMsgTimer);
  recipientsMsgTimer = setTimeout(() => {
    if (recipientsMsg) recipientsMsg.textContent = '';
  }, 2200);
}
function autoSaveRecipients(): void {
  const emails: string[] = [];
  let invalid = false;
  for (const el of recipientInputs) {
    const v = el.value.trim();
    const bad = v !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    el.classList.toggle('invalid', bad);
    if (bad) invalid = true;
    else if (v) emails.push(v);
  }
  if (invalid) {
    flashRecipientsMsg('err', 'Fix the highlighted email to save.');
    return; // preserve the last saved list until the field is valid
  }
  void api
    .setReportRecipients(emails, Boolean(recipientsOptin?.checked))
    .then(() => flashRecipientsMsg('ok', 'Saved'))
    .catch(() => flashRecipientsMsg('err', 'Couldn’t save.'));
}
recipientInputs.forEach((el) => el.addEventListener('change', autoSaveRecipients));
recipientsOptin?.addEventListener('change', autoSaveRecipients);
// Fields load read-only (Chrome never autofills read-only inputs → no group-fill); drop read-only when
// the user clicks into a field, so at most the ONE focused field can ever be autofilled.
recipientInputs.forEach((el) => el.addEventListener('focus', () => el.removeAttribute('readonly'), { once: true }));

// After a scored run, pull the rep's prior same-call-type points and re-render the panel as a series.
// Best-effort; on any failure the single-call view stays.
async function hydrateTrendSeries(): Promise<void> {
  if (!lastResult || !resultEls.trend || !isTrendsEnabled()) return;
  const repName = session?.participants?.rep_name ?? '';
  if (!repName) return;
  try {
    // Pass the current call's type so the server scopes the series to its SCORECARD GROUP (the
    // early-stage group — Intro/Discovery/Business Case/Product Demo — trends together). This matches
    // the Coaching Trends window, so the graph and the "this rep's last N calls" tell one story.
    const older = await api.getTrends(repName, session?.call_meta?.call_type ?? null);
    if (lastResult && resultEls.trend) renderTrend(resultEls.trend, lastResult, older);
  } catch {
    /* keep the single-call fallback */
  }
}

const reportRoot = $('report');
// The MEDDPICC qualification box is an optional, separately-defined container. Resolve it leniently
// (it may not be present yet); the panel renders only when the host page provides #meddpicc.
const meddpiccRoot = document.getElementById('meddpicc');
// Clear the MEDDPICC scan from screen. Invoked on every fresh-start path: new import, Stop, Reset,
// and Clear — so stale qualification findings never linger across calls.
function resetMeddpicc(): void {
  clearMeddpicc(meddpiccRoot);
}

// Coaching Summary (Quick Snapshot page). Auxiliary + non-scoring. The three sub-sections (Score
// Analysis / Trends / Recommendations) are ALWAYS on screen: idle scaffold before a score, a loading
// line during the fetch, filled content after. Fetched AFTER the score renders (never delays it).
const coachingRoot = document.getElementById('coaching-summary');
// "Where do we go from here?" renders here, below the relocated Recent Trends section.
const coachingNext = document.getElementById('coaching-next');
// The transcript we last fetched coaching for — guards against re-fetching on weight changes (coaching
// is transcript-based, not weight-based) and against duplicate in-flight fetches for the same call.
let coachingFor = '';
// The call we last raised the "couldn't score N skills" Message Center alert for — so a weight
// re-aggregate (which re-runs applyResult with the SAME transcript) doesn't repeat the alert. The set
// of unscored skills is fixed at scoring time, so once per call is right.
let unscoredAlertedFor = '';
function clearCoachingSection(): void {
  renderCoachingIdle(coachingRoot, coachingNext); // back to the idle scaffold (headers stay visible)
  coachingFor = '';
}
function loadCoaching(): void {
  if (!coachingRoot || !session || !transcript.trim()) return;
  const sc = table.read();
  if (!sc) return;
  if (coachingFor === transcript) return; // already loaded / loading for this exact call
  coachingFor = transcript;
  const forThis = transcript; // capture so a call that changed mid-fetch doesn't paint stale coaching
  // The first coaching for a call is a live model call (~several seconds); show a loading line so the
  // wait reads as intentional, not a lag. Cached re-views resolve instantly and skip past it.
  coachingLoading(coachingRoot, coachingNext);
  void api
    .getCoaching(transcript, sc, session.call_meta?.call_type ?? '', session.participants?.rep_name ?? '')
    .then((r) => {
      if (forThis !== transcript) return; // the call changed while we were fetching — drop this result
      lastUnscored = r.unscored ?? r.coaching?.unscored ?? []; // feed the pill-hover detail below the cockpit
      if (r.coaching) {
        if (r.unscored) r.coaching.unscored = r.unscored;
        renderCoaching(coachingRoot, coachingNext, r.coaching);
      } else if (r.unscored && r.unscored.length) {
        // No model synthesis (e.g. a call too thin to coach), but we can still explain the blank gaps.
        renderCoaching(coachingRoot, coachingNext, { tldr: '', worked: [], improve: [], unscored: r.unscored });
      } else clearCoachingSection(); // nothing available — back to the idle scaffold
    })
    .catch(() => {
      if (forThis === transcript) clearCoachingSection(); // failure → idle scaffold, allow retry
    });
}
function currentMeta(): ReportMeta {
  return {
    repName: els.repName.textContent ?? '',
    repRole: els.repRole.textContent ?? '',
    businessName: els.businessName.textContent ?? '',
    callType: els.callType.textContent ?? '',
    callDuration: els.callDuration.textContent ?? '',
  };
}

// Message Center = a DOS/terminal log: each line is prefixed with a ">" prompt (CSS) and the text
// is revealed character-by-character (typewriter). Purely client-side — textContent only (safe),
// no extra tokens. The "> " prompt is a CSS ::before, so it shows instantly while the text types in.
function notify(level: string, message: string): void {
  // Recency colour: demote the prior newest line to grey, paint this one gold (.note-latest).
  els.notif.querySelector('.note-latest')?.classList.remove('note-latest');
  const div = document.createElement('div');
  div.className = `note note-${level} note-latest`;
  els.notif.appendChild(div);
  typewrite(div, message);
  els.notif.scrollTop = els.notif.scrollHeight;
}
function typewrite(el: HTMLElement, text: string, onDone?: () => void): void {
  let i = 1;
  const tick = (): void => {
    if (!el.isConnected) return; // box was cleared mid-type, stop quietly
    el.textContent = text.slice(0, i);
    els.notif.scrollTop = els.notif.scrollHeight;
    if (i < text.length) {
      i++;
      window.setTimeout(tick, 9);
    } else {
      onDone?.();
    }
  };
  tick();
}

// A "live" progress line for a multi-second wait (classify, score): types the message, then appends
// one dot every second so the app visibly ticks instead of looking frozen. The message must end with
// "…", which the live dots replace; the first dot lands the moment typing finishes (task start).
// Only one live line runs at a time — starting a new one, or stopLivePending(), freezes the previous
// at its current dot count. Purely client-side (no tokens); self-heals if the box is cleared mid-tick.
const LIVE_MAX_DOTS = 90; // ~90s guard so a hung task can't grow the line without bound
let livePending: { stop: () => void } | null = null;
function stopLivePending(): void {
  livePending?.stop();
  livePending = null;
}
function notifyPending(level: string, message: string): void {
  stopLivePending(); // never tick two live lines at once
  els.notif.querySelector('.note-latest')?.classList.remove('note-latest');
  const div = document.createElement('div');
  div.className = `note note-${level} note-latest`;
  els.notif.appendChild(div);
  const base = message.replace(/…+\s*$/, ''); // drop the trailing ellipsis, live dots take its place
  let dots = 0;
  let timer = 0;
  const paint = (): void => {
    if (!div.isConnected || dots >= LIVE_MAX_DOTS) {
      window.clearInterval(timer);
      return;
    }
    dots++;
    div.textContent = `${base}${'.'.repeat(dots)}`;
    els.notif.scrollTop = els.notif.scrollHeight;
  };
  // Type the base text first, then start the per-second dots — first dot immediately on task start.
  typewrite(div, base, () => {
    paint();
    timer = window.setInterval(paint, 1000);
  });
  livePending = { stop: () => window.clearInterval(timer) };
}
function clearNotes(): void {
  els.notif.innerHTML = '';
}
// Set a Call Details field: text + a full-value tooltip. The value is visually truncated with an
// ellipsis (CSS) when long, so the title lets the user hover to read the whole string.
function setField(el: HTMLElement, val: string): void {
  setText(el, val);
  if (val) el.title = val;
  else el.removeAttribute('title');
}
// Paint the Call Details strip + lifecycle stage from a classified session. Shared by import and by
// snapshot restore, so a restored diagnostic shows exactly what the original import showed.
function applyCallDetails(s: ImportResponse, text: string): void {
  // Rep's Role is hidden when the classifier couldn't reliably derive it (the server also posts a
  // Message Center note).
  const role = s.call_meta?.owner ?? '';
  setField(els.repRole, role);
  els.repRole.closest('.cd-field')?.classList.toggle('cd-hidden', !role);
  setField(els.callType, s.call_meta?.call_type ?? '');
  setField(els.repName, s.participants?.rep_name ?? '');
  setField(els.businessName, s.participants?.business_name ?? '');
  // Lifecycle journey: light up the identified stage in gold (greyed if not identified).
  lifecycle.highlight(s.call_meta?.stage); // from the Stage Identifier classifier
  const mins = estimateDurationMin(text);
  setField(els.callDuration, mins !== null ? `${mins} min` : '');
}

function clearCallDetails(): void {
  setField(els.repName, '');
  setField(els.repRole, '');
  els.repRole.closest('.cd-field')?.classList.remove('cd-hidden'); // restore the row for the next call
  setField(els.businessName, '');
  setField(els.callType, '');
  setField(els.callDuration, '');
  lifecycle.reset(); // all stage boxes greyed (default state)
}
// --- Quick Snapshot in-page toolbar --------------------------------------------------------------
// Its own Run/Stop/Clear/PDF (same handlers as the shared strip), a live status word, and a Call
// Details line. Driven by setPhase alongside the shared buttons so both stay in lock-step.
const skRunBtn = document.getElementById('sk-run-btn') as HTMLButtonElement | null;
const skStopBtn = document.getElementById('sk-stop-btn') as HTMLButtonElement | null;
const skClearBtn = document.getElementById('sk-clear-btn') as HTMLButtonElement | null;
const skPdfBtn = document.getElementById('sk-pdf-btn') as HTMLButtonElement | null;
const skEmailBtn = document.getElementById('sk-email-btn') as HTMLButtonElement | null;
const skImportBtn = document.getElementById('sk-import-btn') as HTMLButtonElement | null; // Quick Snapshot's own Import
const skAnonToggle = document.getElementById('sk-anon-toggle') as HTMLInputElement | null;
const skProgressText = document.getElementById('sk-progress-text');
const callbarProgressText = document.getElementById('callbar-progress-text'); // STATUS on the shared One-Off toolbar
// Quick Snapshot page title — states appended by the score-reaction panel (Re-running / Re-run completed /
// Pending review). CSS uppercases it, so "Quick Snapshot · Re-running" renders as "QUICK SNAPSHOT · RE-RUNNING".
const skPageTitle = document.querySelector('#sk-toolbar .sk-page-title') as HTMLElement | null;
function setSkTitle(state: string): void {
  if (skPageTitle) skPageTitle.textContent = state ? `Quick Snapshot · ${state}` : 'Quick Snapshot';
}
// The imported transcript's file name, shown as a second line in the Quick Snapshot Call Details.
let importedFileName = '';

const SK_STATUS: Record<Phase, string> = {
  idle: 'Idle',
  importing: 'Reading',
  imported: 'Ready',
  running: 'Scoring',
  done: 'Done',
};
function setSkProgress(phase: Phase): void {
  const label = SK_STATUS[phase]; // the status value breathes via CSS
  if (skProgressText) skProgressText.textContent = label;
  if (callbarProgressText) callbarProgressText.textContent = label; // shared toolbar (Cross-Skill / Buyer / MEDDPICC)
}
// --- Call date: display + the "Set call date" control (Option A) ---------------------------------
// The date shows in Call Details as "Taken: <date>", or "Taken: Not Available" when we could not find
// one. Setting it (or correcting a parse) writes the call time onto session.call_meta so a later Run
// sends it, and — when the call is already scored — persists it to Trends via /api/call-date, moving
// the point onto the real time axis. Times are handled in UTC to match the transcript's own clock.
function msToInputValue(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
function inputValueToMs(v: string): number | null {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isFinite(ms) ? ms : null;
}

function openDateEditor(anchor: HTMLElement, phase: Phase, current: number | null): void {
  anchor.textContent = '';
  const input = document.createElement('input');
  input.type = 'datetime-local';
  input.className = 'sk-cd-date-input';
  if (current != null) input.value = msToInputValue(current);
  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'sk-cd-date-save';
  save.textContent = 'Save';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'sk-cd-date-cancel';
  cancel.textContent = 'Cancel';
  anchor.append(input, save, cancel);
  input.focus();
  cancel.addEventListener('click', () => { callBar?.refresh(); });
  save.addEventListener('click', () => {
    const ms = inputValueToMs(input.value);
    if (ms == null) { input.classList.add('is-bad'); return; }
    if (session?.call_meta) { session.call_meta.call_time_ms = ms; session.call_meta.call_time_source = 'user'; }
    // Already scored → persist to Trends now (best-effort). Not yet run → the value rides along on Run.
    if (phase === 'done' && transcript) void api.setCallDate(transcript, ms).then(() => hydrateTrendSeries()).catch(() => {});
    callBar?.refresh(); // the shared session.call_meta changed → update the lens rail's Taken line too
  });
}

/** Render "Taken: <date>" (or "Not Available") on its OWN line beneath the Call Details summary, plus a
 *  small control to set/correct it. Kept off the summary line so the strip stays compact and never wraps. */
function appendCallDate(container: HTMLElement, phase: Phase): void {
  const cm = session?.call_meta;
  if (!cm) return;
  const ms = cm.call_time_ms;
  const known = typeof ms === 'number';
  if (!known && cm.call_time_source !== 'none') return; // legacy call: no call-time info at all
  const wrap = document.createElement('div');
  wrap.className = 'sk-cd-date';
  const label = document.createElement('span');
  label.className = 'sk-cd-date-label';
  label.textContent = 'Taken: ';
  wrap.appendChild(label);
  if (known) {
    wrap.appendChild(document.createTextNode(fmtCallTime(ms)));
  } else {
    const na = document.createElement('span');
    na.className = 'sk-cd-na';
    na.textContent = 'Not Available';
    wrap.appendChild(na);
  }
  const set = document.createElement('button');
  set.type = 'button';
  set.className = 'sk-cd-date-set';
  set.textContent = known ? 'Edit Timestamp' : 'Set Timestamp';
  set.addEventListener('click', () => openDateEditor(wrap, phase, known ? ms : null));
  wrap.append(document.createTextNode(' '), set);
  container.appendChild(wrap);
}

// ---- Diagnostic Scanner (Home runtime monitor) ----------------------------------------------------
// The scanner lives on Home and rides the scoring phase (setPhase → scanLabPhase). Eleven laser heads
// sweep the transcript on a lean 3D conveyor; on completion it swaps the Home header, shows a completion
// message, holds 3s, fades, then hands off to Quick Snapshot. Mounted once at init.
const homeView = document.querySelector('section.view-home') as HTMLElement | null;
const homeHeader = document.getElementById('home-hero-h');
const labHost = document.getElementById('distill-lab');
const HOME_HEADER_TEXT = homeHeader?.textContent ?? 'You are about to dive deep into your secret sauce';
// Two explicit lines (rendered via white-space: pre-line on the lab-mode header). Break after "file".
const SCAN_HEADER_TEXT = "We're classifying and diagnosing your file";
// The "Coaching Signal" gold sliding tab is retired by request — kept wired but never auto-shown after a
// score (see applyResult). Flip to true to bring it back without rewiring.
const SHOW_COACHING_SIGNAL = false;
let labActive = false;
let scannerStopping = false; // true while the Stop button runs its own hold→fade→Home sequence
function enterLabMode(): void {
  labActive = true;
  if (homeHeader) homeHeader.textContent = SCAN_HEADER_TEXT;
  homeView?.classList.add('lab-mode');
}
function exitLabMode(): void {
  labActive = false;
  homeView?.classList.remove('lab-mode');
  homeView?.classList.remove('view-fading');
  if (homeHeader) homeHeader.textContent = HOME_HEADER_TEXT;
  resetScanLab();
}
// Import → Home cross-fade: hold ~1s on the normal Home, then fade it out, swap in the scanner (lab-mode),
// fade back in, and start scoring. Timers only (no rAF dependency) so it runs regardless of tab state.
function enterLabWithTransition(start: () => void): void {
  window.setTimeout(() => {
    homeView?.classList.add('view-fading');                                    // fade the normal Home out
    window.setTimeout(() => {
      enterLabMode();                                                          // swap in the scanner
      window.setTimeout(() => homeView?.classList.remove('view-fading'), 30);  // next tick → fade back in
      start();                                                                 // begin scoring → scanner animates
    }, 520);
  }, 1000);
}
// Group-page import → straight to the scanner: no Home hold, no normal-Home flash. Enter lab-mode while
// faded, then fade the scanner in and start scoring.
function enterLabDirect(start: () => void): void {
  homeView?.classList.add('view-fading');
  enterLabMode();
  window.setTimeout(() => homeView?.classList.remove('view-fading'), 30);
  start();
}
// Fade a destination view IN (restart the one-shot CSS animation) so a hand-off isn't a hard cut.
function fadeInView(name: string): void {
  const v = document.querySelector(`.view[data-view="${name}"]`) as HTMLElement | null;
  if (!v) return;
  v.classList.remove('sl-fadein'); void v.offsetWidth; v.classList.add('sl-fadein');
  window.setTimeout(() => v.classList.remove('sl-fadein'), 700);
}
// Stop button (below the conveyor): abort the in-flight score, hold ~1s, cinematically fade the whole
// view (header + scanner), then return to the normal Home. `scannerStopping` suppresses setPhase's
// auto exit-on-idle so this controlled sequence isn't cut short by the abort's setPhase('idle').
function handleScannerStop(): void {
  scannerStopping = true;
  onStop(); // abort import/run so nothing completes, banners, or navigates after we leave
  window.setTimeout(() => {
    homeView?.classList.add('view-fading');
    window.setTimeout(() => {
      navigateTo('home');
      exitLabMode();
      scannerStopping = false;
      window.setTimeout(() => homeView?.classList.remove('view-fading'), 30);
    }, 520);
  }, 1000);
}
if (labHost) {
  mountScanLab(labHost, {
    onScored: () => { if (homeHeader) homeHeader.textContent = 'Analysis complete'; },
    onComplete: () => {
      homeView?.classList.add('view-fading');                    // fade header + scanner together
      window.setTimeout(() => {
        navigateTo('diagnostic'); fadeInView('diagnostic'); exitLabMode(); // cross-fade into Quick Snapshot
      }, 620);
    },
    onStop: () => handleScannerStop(),
  });
}

let currentPhase: Phase = 'idle';
function setPhase(phase: Phase): void {
  currentPhase = phase;
  // Button-enable policy is the pure controlStates() (unit-tested in controls.test.ts); this just
  // maps the result onto the DOM.
  const s = controlStates(phase, { dirty, hasContent });
  (els.importBtn as HTMLButtonElement).disabled = s.import;
  els.runBtn.disabled = s.run;
  els.stopBtn.disabled = s.stop;
  els.pdfBtn.disabled = s.pdf;
  els.emailBtn.disabled = s.pdf;
  els.clearBtn.disabled = s.clear;
  // Save/Reset are dirty-gated (controls.ts); Save also requires an identified, customizable call type.
  if (els.saveBtn) els.saveBtn.disabled = s.save || !customizable;
  els.resetBtn.disabled = s.reset;
  // Run is a re-score control (adjust the scorecard, then Run to see the impact): enabled only in
  // 'done', greyed on import + while scoring. The first score is automatic (auto-run on import).
  if (skRunBtn) skRunBtn.disabled = s.run;
  if (skStopBtn) skStopBtn.disabled = s.stop;
  if (skClearBtn) skClearBtn.disabled = s.clear;
  if (skPdfBtn) skPdfBtn.disabled = s.pdf;
  if (skEmailBtn) skEmailBtn.disabled = s.pdf;
  if (skImportBtn) skImportBtn.disabled = s.import;
  setSkProgress(phase);
  runtimeProgress(phase); // the trickle bar under the Runtime Status box
  // Only drive the scanner while it is actually on screen (the import / re-run flow puts Home in lab-mode).
  // Otherwise an in-place re-score, a Reset, or a restored 'done' on page load would run it HIDDEN, and its
  // completion would navigate + fade the page on its own — the "phantom refresh" flicker.
  if (labActive || phase === 'idle') scanLabPhase(phase);
  if (labActive && phase === 'idle' && !scannerStopping) exitLabMode(); // a failed import / Clear returns Home to normal
  // Lock the scorecard editor while busy, or when the call type isn't identified (no customizing).
  table.setLocked(s.busy || !customizable);
  // Anonymize is a pre-Run decision. Freeze it only while a job is in flight (importing/running); it
  // stays live whenever Import is (idle / imported / done), so after a score you can flip it for the
  // NEXT import without having to Clear first. Both toolbars' toggles track this.
  if (anonToggle) anonToggle.disabled = s.busy;
  if (skAnonToggle) skAnonToggle.disabled = s.busy;
  // Keep the shared Call context bar in sync with the current call + busy state (import/clear/run).
  callBar?.refresh();
}
let callBar: { refresh: () => void } | null = null;

// --- refresh survival ------------------------------------------------------
// Persist the live diagnostic so a hard refresh restores it instead of wiping it. Best-effort and
// deliberately silent: a storage failure (private mode, quota) must never disturb a run — the user
// simply gets the old behaviour. Called after import, after every result, and on scorecard edits.
let persistWarned = false;
function persistNow(): void {
  if (!session || !transcript) return;
  const ok = saveDiagnostic({
    transcript,
    session,
    scorecard: table.read(),
    baseline: resetBaseline,
    result: lastResult,
    meddpicc: lastMeddpicc,
    customizable,
    buyerSide: getBuyerResult(),
    fileName: importedFileName,
  });
  // Warn once per session if the transcript is simply too big to hold — otherwise the user would
  // believe their work is safe across a refresh when it isn't.
  if (!ok && !persistWarned) {
    persistWarned = true;
    notify('warn', 'This transcript is too large to hold in browser storage, a page refresh will lose the on-screen diagnostic.');
  }
}

// Rebuild the whole screen from a saved snapshot. Runs once at startup, before any user interaction.
// Returns true when a diagnostic was restored.
function restoreDiagnostic(): boolean {
  const snap = loadDiagnostic();
  if (!snap) return false;
  try {
    session = snap.session;
    transcript = snap.transcript;
    lastResult = snap.result;
    lastMeddpicc = snap.meddpicc;
    restoredBuyerResult = snap.buyerSide ?? null; // re-rendered once buyerSide is constructed
    customizable = snap.customizable;
    hasContent = true;
    resetBaseline = snap.baseline;
    importedFileName = snap.fileName ?? ''; // before setPhase() → the Call Details "Transcript:" line

    applyCallDetails(snap.session, snap.transcript);
    if (snap.scorecard) {
      table.load(snap.scorecard);
      const tid = snap.scorecard.template_id ?? '';
      table.setScorecardName(tid !== '' && !tid.startsWith('tmpl-') ? 'Custom' : null);
    }
    if (snap.meddpicc) renderMeddpicc(meddpiccRoot, snap.meddpicc);

    if (snap.result) {
      applyResult(snap.result);
      // The Runtime Monitor is driven by a live event stream that no longer exists — settle every
      // skill the result actually scored to 'done' rather than leaving the board misleadingly idle.
      for (const row of snap.result.skill_breakdown) {
        if (row.evidence_state !== 'not_applicable') board.set(row.skill_id, 'done');
      }
    }
    updateDirty();
    setPhase(snap.result ? 'done' : 'imported');
    const fromFile = importedFileName ? ` from ${importedFileName}` : '';
    notify(
      'info',
      snap.result
        ? `Restored your last diagnostic${fromFile} (final score ${snap.result.final_score}). Nothing was re-scored.`
        : `Restored your imported call${fromFile}. Click Run to score it.`,
    );
    return true;
  } catch {
    // A snapshot we can't apply is worse than none — drop it and start clean.
    restoredBuyerResult = null; // don't restore a buyer read for a diagnostic we're abandoning
    clearDiagnostic();
    return false;
  }
}

async function doImport(name?: string): Promise<void> {
  if (!transcript.trim()) {
    notify('warn', 'Attach or drop a transcript file first.');
    return;
  }
  // Wipe the previous call's data immediately so nothing from the old run bleeds into the new one.
  // Dropping `session` here also hides the (stale) call-details strip during classification, so the
  // previous call's details are never shown against the file now being imported.
  lastResult = null;
  lastMeddpicc = null;
  session = null;
  clearDiagnostic(); // including the stored copy, so a failed import can't restore the old call
  clearNotes();
  clearResults(resultEls);
  clearCallDetails();
  resetMeddpicc();
  clearCoachingSection(); // the call changed → drop any prior coaching summary
  table.clearActual();
  board.resetIdle();
  setSkTitle(''); // a fresh call → reset any Re-running / Re-run completed / Pending review state
  hasContent = true; // a transcript is now in play → Clear becomes available
  buyerSide.reset(); // the shared call changed, clear any stale buyer-lens results for the new call
  if (name) notify('info', `File imported: ${name}.`);
  notifyPending('info', 'Classifying…'); // its own line (gets a ">" prompt) + the per-second dot ticker
  setPhase('importing');
  buyerSide.setImporting(true); // Buyer Signals idle → live "please wait" heartbeat while classifying
  importAbort = new AbortController();
  try {
    session = await api.importTranscript(transcript, importAbort.signal, anonToggle?.checked === true);
    void refreshAccountGate(); // this import may have consumed the free call → refresh the gate for next time
    // When anonymized in-app, the server hands back the SCRUBBED text — score/persist that, not the
    // raw (the raw never gets held or snapshotted once anonymized).
    if (session.transcript) transcript = session.transcript;
    for (const n of session.notifications) notify(n.level, n.message);
    // Call Details are display-only — populated by the classifier (inline text).
    applyCallDetails(session, transcript);
    if (session.template) {
      // Customizable only when the call type was identified; an unidentified call gets the Default
      // scorecard applied read-only (no customizing, per spec).
      customizable = Boolean(session.call_meta?.call_type);
      table.load(session.template);
      // The worker auto-applies the manager's saved scorecard for this call type when one exists.
      // System templates use 'tmpl-*' ids; a user's scorecard uses a UUID → label it CUSTOM.
      const tid = session.template.template_id ?? '';
      const isCustom = tid !== '' && !tid.startsWith('tmpl-');
      table.setScorecardName(isCustom ? 'Custom' : null); // "Scorecard (Custom)" vs "(system default)"
      resetBaseline = structuredClone(session.template); // baseline for Reset (the applied scorecard)
      updateDirty();
      setPhase('imported');
      persistNow(); // an imported-but-unscored call survives a refresh too
      if (autoRunOnImport) {
        // Home quick-start: score immediately (onRun shows its own progress). Deferred one tick so this
        // import's finally (stops the classify ticker) runs before the run's own progress line.
        window.setTimeout(() => {
          if (currentPhase === 'imported') void onRun();
        }, 0);
      } else {
        // In-page import: RUN stays a normal button — we GUIDE the user to click it via the message
        // center rather than auto-running or restyling Run. The scorecard hint doubles as the prompt.
        notify(
          'info',
          customizable
            ? 'Call classified and scorecard applied. Adjust the minimums if you like, then click the ▶ Run button to score.'
            : 'Call classified; the default scorecard applies (read-only). Click the ▶ Run button to score.',
        );
      }
    } else {
      customizable = false;
      setPhase('idle'); // sanity-check failed
    }
  } catch (e) {
    const code = (e as { code?: string }).code;
    if ((e as Error).name === 'AbortError') {
      notify('warn', 'Import cancelled.');
      transcript = ''; // discard the file so a stale transcript cannot be scored
    } else if (code === 'TRIAL_EXHAUSTED' || code === 'BILLING_REQUIRED') {
      // Out of free calls (or billing lapsed): the import never classified. Show a clear modal with a
      // path to Billing instead of the silent bounce-to-Home this used to produce, and discard the file
      // so nothing half-imported lingers (the user re-imports after adding calls).
      transcript = '';
      notify('warn', (e as Error).message); // also leaves a trace in the message center
      openTrialModal(code);
    } else {
      notify('error', (e as Error).message);
    }
    setPhase('idle');
  } finally {
    stopLivePending(); // classify finished (or aborted/errored), freeze the ticking dots
    buyerSide.setImporting(false); // classify done → drop the heartbeat (idle now reflects the result)
    importAbort = null;
  }
}

// Run = "produce a score for the current transcript + current settings." It first tries the FREE
// path (recompute from cached skill scores via /api/aggregate); only if a skill isn't cached yet
// does it fall back to a full model scoring run. So the first Run scores (tokens), and every
// re-run after a settings change is free. Greyed only while a run is in progress.
// Autonomous re-run of red-framed skills: a skill that ends gated_out/failed is never left as a
// solid red frame for the user to manually Run. The app re-runs it itself (bounded rounds), notifies
// each round, and — because finished skills are cached — a re-run only re-calls the skills that
// aren't done yet (no collision with the others). After the bound, leftovers go to a muted,
// non-red "unavailable" state; the overall score still completes (failed skills degrade in the gate).
const MAX_AUTO_ROUNDS = 3;
let autoRound = 0;
const runErrors = new Set<string>(); // skills currently red in the latest pass

async function onRun(): Promise<void> {
  if (!session || !table.read()) return;
  const wasEdited = dirty; // re-running an edited scorecard → invite Restore once it completes
  setRecalcNeeded(false); // running clears the "hit RUN" prompt + Run-button pulse
  notifyPending('info', 'Running, scoring in progress…');
  autoRound = 0; // a fresh user-initiated Run resets the autonomous-retry budget
  await scoreLoop();
  setRestorePulse(wasEdited); // recalculated an edited scorecard → flash RESTORE ORIGINAL
}

// One scoring pass: the FREE re-aggregate from cache when every skill is cached, else a full streamed
// run (cached skills return instantly, so only not-yet-done skills actually re-call the model).
async function scoreLoop(): Promise<void> {
  const scorecard = table.read();
  if (!session || !scorecard) return;
  setPhase('running');
  hideCoachingSignal(); // a fresh run supersedes the last score's banner; re-shown on the new result
  // Kick off Buyer Signals in parallel so its Runtime Monitor appears the moment Run is hit on ANY
  // lens (not only after seller scoring finishes). Self-guards: no-op if already running / has results.
  buyerSide.autoRun();
  runErrors.clear();
  runAbort = new AbortController();
  try {
    let agg: { result?: AggregateResult; needs_scoring?: boolean; meddpicc?: MeddpiccReport } = { needs_scoring: true };
    try {
      agg = await api.reaggregate(transcript, scorecard);
    } catch {
      /* fall through to a full scoring run */
    }
    if (agg.result) {
      // The cheap re-aggregate re-scores from cached skills only; it does NOT regenerate the LLM
      // narrative (high_level_summary → the Cross-Skill lens) or MEDDPICC. Carry the previously
      // computed ones over so a re-run of an already-scored call doesn't blank them.
      if (!agg.result.high_level_summary && lastResult?.high_level_summary) {
        agg.result.high_level_summary = lastResult.high_level_summary;
      }
      // The re-aggregate path returns the cached MEDDPICC report (scored during the original Run); use
      // it so a re-run of an already-scored call keeps its qualification panel.
      if (agg.meddpicc) lastMeddpicc = agg.meddpicc;
      // Only take the fast path when we actually have a qualification report to show. If the original
      // Run never cached one (its scan failed, or an older run predates MEDDPICC caching) AND we have no
      // in-session copy, fall THROUGH to the full run below: it serves the cached skills instantly and
      // only actually scans the missing MEDDPICC, so the panel is never left blank.
      if (lastMeddpicc) {
        applyResult(agg.result);
        renderMeddpicc(meddpiccRoot, lastMeddpicc);
        finalizeRun();
        return;
      }
      notify('info', 'Generating the MEDDPICC qualification for this call…');
    }
    clearResults(resultEls);
    table.clearActual();
    board.resetIdle();
    // Cosmetic pre-flight: flicker the Sanity Check box gold for ~2s while the skills start scoring.
    void board.simulateSanityCheck();
    let terminal: RunEvent['t'] | null = null;
    const trendCtx = { repName: session.participants?.rep_name ?? undefined, callType: session.call_meta?.call_type ?? undefined, stage: session.call_meta?.stage ?? undefined, synthetic: session.synthetic, anonymizedInApp: session.anonymized_in_app, includeInBenchmark: benchPrefFor(transcript), callTimeMs: session.call_meta?.call_time_ms ?? undefined };
    for await (const ev of api.runScoring(session.session_id, transcript, scorecard, runAbort.signal, false, trendCtx)) {
      handleEvent(ev);
      if (ev.t === 'aggregate_ready' || ev.t === 'run_error' || ev.t === 'run_stopped') {
        terminal = ev.t;
        break;
      }
    }
    if (terminal === 'aggregate_ready') await afterPass();
    // run_error / run_stopped already set the phase in handleEvent.
  } catch (e) {
    stopLivePending(); // scoring pass errored/aborted, freeze the ticking dots
    if ((e as Error).name !== 'AbortError') notify('error', (e as Error).message);
    setPhase(lastResult ? 'done' : 'imported');
  }
}

// After a completed pass: auto re-run any still-red skill (no manual Run) until they clear or the
// round budget is spent — then finalize.
async function afterPass(): Promise<void> {
  if (runErrors.size > 0 && autoRound < MAX_AUTO_ROUNDS) {
    autoRound++;
    notify('info', `Automatic LLM retry, re-running ${runErrors.size} skill(s) that didn't complete (round ${autoRound}/${MAX_AUTO_ROUNDS})…`);
    await new Promise((r) => setTimeout(r, 800)); // brief spacing so a transient limit can clear
    await scoreLoop();
    return;
  }
  finalizeRun();
}

function finalizeRun(): void {
  stopLivePending(); // run complete, freeze the "scoring in progress" dots
  if (runErrors.size > 0) {
    for (const id of runErrors) board.markUnavailable(id); // muted, non-red, never a solid red frame
    notify(
      'warn',
      `${runErrors.size} skill(s) could not be scored after ${MAX_AUTO_ROUNDS} automatic attempts, marked unavailable. The overall score still completed.`,
    );
  }
  setPhase('done');
  if (lastResult) {
    notify(
      'info',
      `Done. Final score ${lastResult.final_score}, ${lastResult.meaning_label}. Before drawing conclusions, explore all three tabs, Quick Snapshot, Buyer Signals, and Cross-Skill.`,
    );
  }
  // Buyer Signals is already running (kicked off at the start of the run so its Runtime Monitor shows
  // immediately). This is a self-guarded fallback in case it wasn't (e.g. a cached re-aggregate).
  if (lastResult) buyerSide.autoRun();
  // Momentum banner hidden for now (2026-08-04 batch): may resurface elsewhere on the page later. The
  // banner machinery stays wired (mount, dismiss, prefs) — just don't auto-show it after a score.
  if (SHOW_MOMENTUM_BANNER && lastResult) maybeShowMomentumBanner();
}

/** The info panel below the cockpit: the static help text by default, swapped to the hovered pill's
 *  coaching detail (same text as the Coaching Summary) while a pill is hovered, and restored to help on
 *  leave (id === null) or when there's no detail for that skill yet. */
function renderPerfInfo(id: string | null): void {
  const help = document.querySelector<HTMLElement>('#perf-info .pi-help');
  const coach = document.querySelector<HTMLElement>('#perf-info .pi-coach');
  if (!help || !coach) return;
  const d = id && lastResult ? skillCoachingDetail(id, lastResult.skill_breakdown, lastUnscored) : null;
  if (!d) { coach.hidden = true; coach.innerHTML = ''; help.hidden = false; return; }
  coach.innerHTML = '';
  const label = document.createElement('strong');
  label.className = `psd-label psd-${d.tone}`;
  setText(label, d.label + ' ');
  const why = document.createElement('span');
  why.className = 'psd-why';
  setText(why, d.why);
  coach.append(label, why);
  help.hidden = true;
  coach.hidden = false;
}

/** Keep the Performance Breakdown card compact (its natural height — never stretched) and instead grow the
 *  info panel below it so the info panel's BOTTOM lines up with the Rep Classification card in the rail.
 *  All the pieces used are scroll-invariant: (rep-bottom − rail-top) is measured inside the sticky rail, and
 *  the card height + the card→info gap are in-flow heights. */
function syncPerfInfoHeight(): void {
  const view = document.querySelector('.view[data-view="diagnostic"]');
  const card = document.querySelector<HTMLElement>('.view[data-view="diagnostic"] .results-b');
  const info = document.getElementById('perf-info');
  if (!view || !view.classList.contains('is-active') || !card || !info) return;
  card.style.minHeight = ''; // the cockpit card stays compact (bottom frame just below the last row)
  const callBar = document.getElementById('call-bar');
  const repClass = document.getElementById('callbar-tb-class');
  if (!callBar || callBar.hidden || !repClass || repClass.hidden) { info.style.minHeight = ''; return; }
  const railToRep = repClass.getBoundingClientRect().bottom - callBar.getBoundingClientRect().top;
  const gap = info.getBoundingClientRect().top - card.getBoundingClientRect().bottom; // margin between card + info
  const needed = Math.round(railToRep - card.getBoundingClientRect().height - gap);
  info.style.minHeight = needed > 0 ? `${needed}px` : '';
}

function applyResult(r: AggregateResult): void {
  renderPerfInfo(null); // back to the default help text for the new score
  lastUnscored = []; // refilled when this call's coaching fetch resolves (loadCoaching below)
  lastResult = r;
  setRecalcNeeded(false); // a fresh score is on screen → the "hit RUN to recalculate" prompt no longer applies
  // Cache this result under the scorecard it was scored with (the current table), so a later Reset back
  // to this scorecard restores instantly without a re-aggregate.
  const scNow = table.read();
  if (scNow) scoredCache.set(scorecardSig(scNow), structuredClone(r));
  renderResults(resultEls, table, r);
  syncBenchmarkUI(); // reflect the stored "Add this call to Benchmark" choice for this transcript
  applyTrendScale(); // the fresh trend SVG needs its 13.5px label scale set
  renderReport(reportRoot, r, currentMeta(), getBuyerResult(), lastMeddpicc);
  persistNow(); // the scored result is the thing a refresh used to destroy
  // Pull the rep's prior same-call-type points and re-render the trend panel as a series (best-effort).
  void hydrateTrendSeries();
  // Fetch the evidence-grounded Coaching Summary for this scored call (best-effort, transcript-guarded
  // so a weight re-aggregate doesn't refetch). Silent-until-ready — failure just leaves the section hidden.
  loadCoaching();
  // Watchdog: every scored shortfall (observed performance below the manager's expected minimum)
  // must appear in "Areas requiring improvement". If any is missing, that section would understate
  // the rep's misses — surface it rather than let it pass silently.
  const missed = missedShortfalls(r.skill_breakdown, improvementGaps(r.skill_breakdown));
  if (missed.length) {
    notify(
      'warn',
      `QC: ${missed.length} under-target skill(s) missing from Areas requiring improvement, ` +
        `${missed.map((s) => displaySkillName(s.skill_name)).join(', ')}. Flagged for review.`,
    );
  }
  // Watchdog: a scored skill the performance bars can't place (unknown skill_id) would paint a
  // misleading 0 instead of its real score. Surface it rather than let a silent 0 mislead the manager.
  const unplaceable = unplaceableScoredSkills(r.skill_breakdown);
  if (unplaceable.length) {
    notify(
      'warn',
      `QC: ${unplaceable.length} scored skill(s) couldn't be shown on the performance bars, ` +
        `${unplaceable.map((s) => displaySkillName(s.skill_name)).join(', ')}. The scores are correct; the display is not. Flagged for review.`,
    );
  }
  // Alert about no-evidence / ungradeable skills, matching how the Performance Breakdown now shows them:
  // a REQUIRED skill (minimum >= 1) with no evidence is a real miss — scored Missing, a full gap below the
  // minimum. A skill that genuinely couldn't be graded (not applicable, too little transcript, or an
  // optional skill with no evidence) shows no gap. Keep the Message Center terse; the per-skill detail is
  // on the Coaching Summary. Once per call (guarded), so a weight re-aggregate doesn't repeat it.
  const total = r.skill_breakdown.length;
  const missing = r.skill_breakdown.filter((s) => s.evidence_state === 'no_evidence' && s.expected_score >= 1);
  const ungraded = r.skill_breakdown.filter(
    (s) => s.evidence_state !== 'scored' && !(s.evidence_state === 'no_evidence' && s.expected_score >= 1),
  );
  if ((missing.length || ungraded.length) && unscoredAlertedFor !== transcript) {
    unscoredAlertedFor = transcript;
    const clauses: string[] = [];
    if (missing.length) {
      const one = missing.length === 1;
      clauses.push(
        `${missing.length} of ${total} skill${total === 1 ? '' : 's'} had no evidence on this call, so ${one ? "it's" : "they're"} marked Missing: a full gap below your minimum.`,
      );
    }
    if (ungraded.length) {
      const one = ungraded.length === 1;
      clauses.push(
        `${ungraded.length} skill${one ? '' : 's'} couldn't be graded from the transcript (not applicable, or too little to go on) and show${one ? 's' : ''} no gap.`,
      );
    }
    notify('alert', `Heads-up, ${clauses.join(' ')} Open the Coaching Summary to see what happened on each.`);
  }
  // Coaching signal: only when one or more scored skills came in below their expected bar. When every
  // skill meets or beats target there is no gap to coach, so the banner stays hidden.
  // Currently NOT shown — the sliding gold tab was retired by request (2026-08 batch). The banner code
  // stays wired (mounted/dismissable) so it can be resurfaced later without rewiring; flip the flag on.
  const gaps = r.skill_breakdown.filter(
    (s) => s.actual_score != null && s.expected_score > 0 && (s.actual_score as number) < s.expected_score,
  );
  if (SHOW_COACHING_SIGNAL && gaps.length) {
    const top = gaps.reduce((a, b) =>
      b.expected_score - (b.actual_score as number) > a.expected_score - (a.actual_score as number) ? b : a,
    );
    showCoachingSignal({ gapCount: gaps.length, topSkill: displaySkillName(top.skill_name) });
  } else {
    hideCoachingSignal();
  }
  requestAnimationFrame(syncPerfInfoHeight); // the rail (score, details, rep class) just filled — re-match heights
}

// --- Reset + dirty tracking (manager-defined settings vs the call-type contract defaults) ---
function computeDirty(): boolean {
  if (!resetBaseline) return false;
  const cur = table.read();
  if (!cur) return false;
  const base = new Map(resetBaseline.skills.map((s) => [s.skill_id, s]));
  for (const s of cur.skills) {
    const b = base.get(s.skill_id);
    if (!b || s.expected_score !== b.expected_score || s.importance !== b.importance) return true;
  }
  return false;
}
function updateDirty(): void {
  dirty = computeDirty();
  const busy = currentPhase === 'running' || currentPhase === 'importing';
  // Save + Reset light up only when there are unsaved edits; Save also needs a customizable call type.
  els.resetBtn.disabled = busy || !dirty;
  if (els.saveBtn) els.saveBtn.disabled = busy || !dirty || !customizable;
  syncTrendTargets();
  persistNow(); // unsaved minimum/weight edits survive a refresh too (no-op before an import)
}

// A target minimum was edited, so the on-screen score is stale until the user Runs. Show the hint next
// to RESTORE ORIGINAL and pulse the Run button; cleared on Run / Reset / Clear, or when the edit is undone.
function setRecalcNeeded(on: boolean): void {
  const hint = document.getElementById('recalc-hint');
  if (hint) hint.hidden = !on;
  skRunBtn?.classList.toggle('pulse-run', on);
  els.runBtn?.classList.toggle('pulse-run', on);
}
// After the user Runs an EDITED scorecard, flash RESTORE ORIGINAL (same heartbeat as Run) to offer a
// one-click return to the applied scorecard.
function setRestorePulse(on: boolean): void {
  els.resetBtn?.classList.toggle('pulse-run', on);
}
function onScorecardEdited(): void {
  updateDirty();
  setRecalcNeeded(dirty); // dirty=false when the edit returns to the applied scorecard → hint hides
  setRestorePulse(false); // a new edit supersedes any restore invitation; they must Run again first
}

// Live-preview: the trend's gold expected-minimum contour tracks the in-progress Scorecard edits
// (unsaved), matching the editable Scorecard bars. Surgical — repoints only the contour, so the drawn
// call lines and their on/off toggle state are untouched. No-op when the chart isn't shown.
function syncTrendTargets(): void {
  if (!lastResult || !resultEls.trend || !isTrendsEnabled()) return;
  const sc = table.read();
  if (!sc) return;
  const targets: Record<string, number | null> = {};
  for (const s of sc.skills) targets[s.skill_id] = s.expected_score;
  updateTrendTargets(resultEls.trend, targets);
}
async function onReset(): Promise<void> {
  if (!resetBaseline) return;
  const baseline = structuredClone(resetBaseline);
  table.load(baseline); // revert the diamonds to the applied scorecard (local only)
  updateDirty(); // dirty is now false → Reset greys out immediately
  setRecalcNeeded(false); // reverted to the applied scorecard → clear the recalc prompt
  setRestorePulse(false); // stop the Restore heartbeat once it has been used
  // The FINAL SCORE depends on the expected minimums (critical drag / coverage), so reverting the
  // diamonds alone would leave the circle showing the last EDITED run's number. INSTANT PATH (zero
  // network): if this exact scorecard was already scored this session, its result is cached — re-apply
  // it. The cache is keyed by the score-affecting fields, so a hit is guaranteed to be the right number
  // for this scorecard (never stale, never a miscalculation).
  const cached = scoredCache.get(scorecardSig(baseline));
  if (cached) {
    applyResult(structuredClone(cached));
    if (lastMeddpicc) renderMeddpicc(meddpiccRoot, lastMeddpicc);
    notify('info', 'Reverted to the applied scorecard.');
    return;
  }
  // RARE FALLBACK: this scorecard was never scored this session (e.g. edited before any Run), so there
  // is no cached number to show — recompute it with the cheap cached re-aggregate (no LLM). If even that
  // can't run, repaint the actual bars (they are scorecard-independent) so the screen isn't left blank.
  if (transcript.trim() && session) {
    try {
      const agg = await api.reaggregate(transcript, baseline);
      if (agg.result) {
        if (!agg.result.high_level_summary && lastResult?.high_level_summary) agg.result.high_level_summary = lastResult.high_level_summary;
        if (agg.meddpicc) lastMeddpicc = agg.meddpicc;
        applyResult(agg.result);
        if (lastMeddpicc) renderMeddpicc(meddpiccRoot, lastMeddpicc);
        notify('info', 'Reverted to the applied scorecard, and re-scored against it.');
        return;
      }
    } catch {
      /* fall through to the bars-only repaint below */
    }
  }
  if (lastResult) table.renderActual(lastResult.skill_breakdown);
  notify('info', 'Reverted to the applied scorecard.');
}

function handleEvent(ev: RunEvent): void {
  switch (ev.t) {
    case 'skill':
      if (ev.state === 'gated_out' || ev.state === 'failed') {
        // Never paint a solid red frame: track it and show it as retrying — the app auto re-runs it.
        runErrors.add(ev.skill_id);
        board.set(ev.skill_id, 'retrying');
      } else {
        if (ev.state === 'done' || ev.state === 'running' || ev.state === 'retrying') runErrors.delete(ev.skill_id);
        board.set(ev.skill_id, ev.state);
      }
      break;
    case 'notification':
      notify(ev.level, ev.message);
      break;
    case 'meddpicc_ready':
      // Auxiliary qualification signal — render it, but it never feeds the score.
      lastMeddpicc = ev.report;
      renderMeddpicc(meddpiccRoot, ev.report);
      persistNow();
      break;
    case 'meddpicc_failed':
      // The scan couldn't complete — show an honest "unavailable" state, not a blank box. Never scored.
      renderMeddpiccUnavailable(meddpiccRoot, ev.reason);
      break;
    case 'aggregate_ready':
      // Render the score/report; phase + the done/unavailable notification are owned by finalizeRun
      // so the autonomous-retry chain isn't interrupted by a premature "done".
      applyResult(ev.result);
      break;
    case 'run_error':
      stopLivePending(); // run failed, freeze the "scoring in progress" dots
      notify('error', ev.message);
      setPhase('imported');
      break;
    case 'run_stopped':
      onStopped();
      break;
  }
}

function onStop(): void {
  stopRuntimeProgress(); // freeze the progress bar where it is (before the abort flows to setPhase('idle'))
  if (currentPhase === 'importing') importAbort?.abort();
  else runAbort?.abort();
  buyerSide.stop(); // shared Stop halts the buyer lens too (it runs as part of "Run everything")
}

function onStopped(): void {
  stopLivePending(); // user stopped mid-run, freeze the ticking dots
  lastMeddpicc = null;
  clearDiagnostic(); // Stop discards partials, they must not come back on refresh
  clearResults(resultEls);
  resetMeddpicc();
  clearCoachingSection();
  table.clearActual();
  board.resetIdle();
  lifecycle.reset(); // stage box returns to the default greyed state on Stop
  notify('warn', 'Process stopped by user. Re-import to run again.');
  loadBlank();
  setPhase('idle');
}

// Clear = wipe the screen back to a fresh start (scores, notifications, recommendations, Call
// Details, Runtime Monitor) so the user can score another call from scratch. Enabled only after a
// successful scoring (phase 'done').
function onClear(): void {
  session = null;
  transcript = '';
  rawTranscript = '';
  lastResult = null;
  lastUnscored = [];
  lastMeddpicc = null;
  hasContent = false; // back to a pristine state → Clear disables again
  clearDiagnostic(); // Clear means cleared from browser storage too, not just off the screen
  clearResults(resultEls);
  renderPerfInfo(null); // no result → back to the default help text
  const info = document.getElementById('perf-info');
  if (info) info.style.minHeight = ''; // drop the rail-matched extra height
  syncBenchmarkUI(); // no result → hide the "Add this call to Benchmark" control
  clearCallDetails();
  resetMeddpicc();
  clearCoachingSection();
  table.clearActual();
  setRecalcNeeded(false); // fresh state → no pending recalc
  setRestorePulse(false);
  board.resetIdle();
  buyerSide.reset(); // no call loaded → buyer lens returns to its empty/import state
  hideCoachingSignal(); // no result → the coaching banner goes away too
  reportRoot.innerHTML = '';
  els.notif.innerHTML = CLEARED_HTML;
  loadBlank();
  setPhase('idle');
  // The strip controls act on the shared call from wherever you are — Clear stays on the current lens
  // (no forced hop to Quick Snapshot).
}

// Reset the scorecard editor to a true "from scratch" state: the skill rows show but with no
// minimums set and neutral weights, and the table is locked (not customizable until a call type is
// identified on import). Used by Clear and Stop, and mirrors the initial idle state.
function loadBlank(): void {
  customizable = false;
  table.load(structuredClone(BLANK_SCORECARD));
  table.setScorecardName(null);
  resetBaseline = structuredClone(BLANK_SCORECARD);
  updateDirty();
}

// Save the current (edited) settings as this call type's Custom scorecard — one click, no name
// prompt (the scorecard IS the call type). Upserts by the applied scorecard's canonical name, so it
// overrides the applied settings and shows in the Scorecards page's Custom column.
async function onSave(): Promise<void> {
  const sc = table.read();
  if (!sc || !customizable) return;
  if (isAllOptional(sc) && !window.confirm(ALL_OPTIONAL_WARNING)) return;
  if (els.saveBtn) els.saveBtn.disabled = true;
  try {
    const mine = await api.listMyTemplates().catch(() => []);
    const existing = mine.find((t) => t.template_name === sc.template_name);
    if (existing) await api.updateTemplate(existing.id, sc);
    else await api.saveTemplate(sc);
    notify('info', 'Saved as your Custom scorecard for this call type.');
    resetBaseline = structuredClone(sc); // the applied scorecard is now this custom one
    table.setScorecardName('Custom'); // header → "Scorecard (Custom)"
    updateDirty(); // matches the new baseline → Save greys out
    void scorecards.refresh(); // reflect it on the Scorecards page
  } catch (e) {
    updateDirty(); // re-enable Save so the user can retry
    notify('error', (e as Error).message);
  }
}

// Cached trial/billing gate, so the Import button can decide SYNCHRONOUSLY whether to open the file
// picker — a native file dialog must open inside the click gesture, so we can't await a fetch first.
// Seeded on load, refreshed after each import (a free call is consumed there) and when the tab regains
// focus (a purchase made in the Lemon checkout tab flips the account to active). null = unknown → allow,
// with the server 402 in doImport as the backstop.
let acctGate: { status: string; freeLeft: number } | null = null;
async function refreshAccountGate(): Promise<void> {
  try {
    const a = await api.getAccount();
    acctGate = { status: a.billingStatus, freeLeft: a.trialActive ? 1 : 0 };
    // Billing status just became known/changed → re-evaluate the CRO Zone paid-plan gate for the page
    // the user is on (e.g. they navigated to Team Trends before /api/account resolved).
    syncCroGate(document.querySelector('.view.is-active')?.getAttribute('data-view') ?? '');
  } catch {
    /* keep the last known gate; unknown stays unknown → allow, server 402 backstops */
  }
}
// The block code if a NEW import would be rejected (billing not active AND no free call left), else null.
function gateBlockCode(): 'TRIAL_EXHAUSTED' | 'BILLING_REQUIRED' | null {
  if (!acctGate || acctGate.status === 'active' || acctGate.freeLeft > 0) return null;
  return acctGate.status === 'trialing' ? 'TRIAL_EXHAUSTED' : 'BILLING_REQUIRED';
}
// The ONE guarded import entry point (every button/link trigger routes through it): gate FIRST, and open
// the shared file picker only when allowed. Out of calls → show the upgrade modal, never open the picker.
function requestImport(): void {
  const code = gateBlockCode();
  if (code) {
    openTrialModal(code);
    return;
  }
  els.fileInput.click();
}

// --- The CRO Zone paid-plan gate ---
// The CRO Zone is available on any paid plan. A free-trial owner still SEES it in the menu; opening any
// CRO page shows an upgrade message over the content instead of the page. This is a client-side
// monetization nudge (the data is the account's own). Allow when the plan is active, or when the billing
// status is not yet known (fail-open; the gate re-syncs once /api/account resolves).
const CRO_VIEWS = new Set(['bespoke-playbook', 'teams', 'team-trends', 'history', 'compare']);
function croLocked(): boolean {
  return !!acctGate && acctGate.status !== 'active';
}
let croGateEl: HTMLElement | null = null;
function ensureCroGate(): HTMLElement | null {
  if (croGateEl) return croGateEl;
  const region = document.querySelector('.app-region');
  if (!region) return null;
  const el = document.createElement('div');
  el.className = 'cro-gate';
  el.id = 'cro-gate';
  el.hidden = true;
  const card = document.createElement('div');
  card.className = 'cro-gate-card';
  const badge = document.createElement('div');
  badge.className = 'cro-gate-badge';
  badge.textContent = 'The CRO Zone';
  const h = document.createElement('h2');
  h.className = 'cro-gate-h';
  h.textContent = 'Unlock team analytics';
  const p = document.createElement('p');
  p.className = 'cro-gate-p';
  p.textContent =
    'The CRO Zone turns your scored calls into team-level insight: rep and team score trends over time, ' +
    'cohort gap analysis, and side-by-side comparisons. It is available on any paid plan. Add a call pack ' +
    'to unlock it, and your free trial, saved reports, and settings stay exactly as they are.';
  const cta = document.createElement('button');
  cta.type = 'button';
  cta.className = 'cro-gate-cta';
  cta.textContent = 'See plans and call packs';
  cta.addEventListener('click', () => navigateTo('billing'));
  card.append(badge, h, p, cta);
  el.appendChild(card);
  region.appendChild(el);
  croGateEl = el;
  return el;
}
function syncCroGate(view: string): void {
  const el = ensureCroGate();
  if (!el) return;
  el.hidden = !(CRO_VIEWS.has(view) && croLocked());
}
onViewChange(syncCroGate);

// Set true by the Home "Import a transcript" button so navigation to Quick Snapshot is deferred until a
// file is actually chosen (the user stays on Home if they cancel the picker). Other-lens imports leave
// it false and stay on their own lens.
let pendingHomeImport = false;
// Every import auto-runs the score (import → straight to a number): the Home quick-start AND any in-page
// toolbar import across all 4 lenses. The user can still adjust the minimums afterwards and click Run to
// re-score. (Previously only a Home import auto-ran; in-page imports stopped at 'imported'.)
let autoRunOnImport = false;
wireFileImport({
  button: els.importBtn,
  dropZone: els.notif,
  input: els.fileInput,
  onFile: (f) => {
    rawTranscript = f.text;
    transcript = f.text;
    importedFileName = f.name;
    autoRunOnImport = true; // auto-run on every import, from Home or any in-page lens toolbar
    // Every import routes to the Home diagnostic scanner. From the Home Import button we hold ~1s on the
    // normal Home first, then cross-fade in; from a group-page toolbar we go STRAIGHT to the scanner (no
    // Home detour). On done it holds, fades, and hands off to Quick Snapshot (see mountScanLab callbacks).
    const fromHome = pendingHomeImport;
    pendingHomeImport = false;
    // Gate here too — this fires for the drag-DROP path, which never went through requestImport(). (Button
    // triggers were already gated before the picker, so this is a no-op for them.) Out of calls → modal,
    // no scanner, discard the file.
    const code = gateBlockCode();
    if (code) {
      transcript = '';
      rawTranscript = '';
      openTrialModal(code);
      return;
    }
    navigateTo('home');
    if (fromHome) enterLabWithTransition(() => void doImport(f.name));
    else enterLabDirect(() => void doImport(f.name));
  },
  onError: (msg) => notify('error', msg),
  // Import button (shared toolbar) → gate before the picker opens.
  beforeOpen: () => {
    const code = gateBlockCode();
    if (code) {
      openTrialModal(code);
      return false;
    }
    return true;
  },
});

els.runBtn.addEventListener('click', () => void onRun());
els.stopBtn.addEventListener('click', onStop);
els.clearBtn.addEventListener('click', onClear);
// Quick Snapshot toolbar buttons drive the SAME global actions as the shared strip.
skRunBtn?.addEventListener('click', () => void onRun());
skStopBtn?.addEventListener('click', onStop);
skClearBtn?.addEventListener('click', onClear);
skPdfBtn?.addEventListener('click', () => void onDownload());
// Quick Snapshot's own Import opens the same picker; onFile keeps the user on Quick Snapshot (not a Home
// import), so it stops at 'imported' and the message center prompts them to click Run.
skImportBtn?.addEventListener('click', () => requestImport());
// "Advanced" toggle (header, left of Quick Help) replaces the former in-toolbar Enablement checkbox.
// ON shows the message center + runtime monitor + lifecycle on Quick Snapshot; OFF hides them. The
// opening state is derived from the Home role — "Other" → ON, CRO / neither selected → OFF — on load
// and each time the user enters via Home Import. Clicking flips THIS page only; it never touches the
// Home role buttons.
const advToggle = document.getElementById('advanced-toggle');
const advState = document.getElementById('advanced-state');
const diagView = document.querySelector('.view[data-view="diagnostic"]');
const crossView = document.querySelector('.view[data-view="cross-skill"]');
// The Message Center is now a sibling of #call-bar (so it scrolls). It shows only on a lens with Advanced
// ON; gate it by both the current view and the Advanced state.
const callbarMsgEl = document.getElementById('callbar-msg');
let callbarMsgView = document.querySelector('.view.is-active')?.getAttribute('data-view') ?? 'home';
function syncCallbarMsg(): void {
  // Quick Snapshot has its OWN Message Center zone (the #sk-notifications copy in its enable section),
  // so the shared strip's Message Center is suppressed there; it shows on every other strip view
  // (Cross-Skill / Buyer Signals / MEDDPICC / Coaching) when Advanced is ON.
  if (callbarMsgEl) callbarMsgEl.hidden =
    !(callBarVisible(callbarMsgView) && advancedOn && callbarMsgView !== 'diagnostic');
}
let advancedOn = false;
function setAdvanced(on: boolean): void {
  advancedOn = on;
  diagView?.classList.toggle('sk-collapsed', !on); // Quick Snapshot: Enablement section
  crossView?.classList.toggle('cs-adv-off', !on); // Cross-Skill: hide the connected-skill diagram
  syncCallbarMsg(); // shared One-Off Message Center (sibling of the toolbar)
  if (advState) advState.textContent = on ? 'ON' : 'OFF';
  advToggle?.setAttribute('aria-checked', on ? 'true' : 'false');
  try {
    sessionStorage.setItem('studio.advanced.v1', on ? '1' : '0'); // survive a hard refresh (per-tab)
  } catch {
    /* private mode — falls back to the role-derived default each load */
  }
}
if (advToggle) {
  // Restore the last Advanced state across a hard refresh; on a fresh tab/session there's no stored
  // value, so it defaults to OFF.
  let storedAdv: string | null = null;
  try {
    storedAdv = sessionStorage.getItem('studio.advanced.v1');
  } catch {
    /* private mode */
  }
  setAdvanced(storedAdv !== null ? storedAdv === '1' : false);
  advToggle.addEventListener('click', () => setAdvanced(!advancedOn));
}
// The Anonymize toggle appears in three places (shared strip, Quick Snapshot toolbar, Home) — all mirror
// one canonical checked state. Flipping any of them syncs the others; if a classified, not-yet-scored
// call is loaded, it re-imports the SAME file under the new setting (so a post-import tick actually
// scrubs it, an untick restores the real call). homeAnonToggle/skAnonToggle are resolved lazily inside,
// so this can be defined before they are declared.
function setAnonChecked(v: boolean): void {
  if (anonToggle) anonToggle.checked = v;
  if (homeAnonToggle) homeAnonToggle.checked = v;
  if (skAnonToggle) skAnonToggle.checked = v;
}
function onAnonChange(v: boolean): void {
  setAnonChecked(v);
  if (currentPhase === 'imported' && rawTranscript.trim()) {
    transcript = rawTranscript;
    void doImport();
  }
}
anonToggle?.addEventListener('change', () => onAnonChange(anonToggle.checked));
skAnonToggle?.addEventListener('change', () => onAnonChange(skAnonToggle.checked));
// "How this score was calculated" label → Getting Started for the full scoring method (its hover
// tooltip carries the explanation; a click opens the full breakdown).
document.getElementById('how-calculated-label')?.addEventListener('click', () => navigateTo('getting-started'));
els.resetBtn.addEventListener('click', () => void onReset());
// Editing expected-minimums or weights updates the Reset dirty state and prompts a manual re-Run.
table.setOnChange(onScorecardEdited);
// Hovering a status pill swaps the below-cockpit info panel from its default help text to that skill's
// coaching detail (same text as the Coaching Summary); leaving the pill restores the help text.
table.setOnPillHover(renderPerfInfo);
// Keep the cockpit card's bottom aligned with the rail's Rep Classification card as either side resizes.
if (typeof ResizeObserver !== 'undefined') {
  const ro = new ResizeObserver(() => requestAnimationFrame(syncPerfInfoHeight));
  const cb = document.getElementById('call-bar');
  if (cb) ro.observe(cb);
}
window.addEventListener('resize', () => requestAnimationFrame(syncPerfInfoHeight));
// Save was removed from this page — customizing/saving a scorecard happens only on the Scorecards
// page. The listener is guarded so it degrades to a no-op (and keeps onSave referenced).
els.saveBtn?.addEventListener('click', () => void onSave());

// --- Recent Trends (relocated into the Coaching block): the title toggles the graphs; the per-skill /
// heatmap buttons are a sibling of the title, so clicking them doesn't collapse the section. ---
const recentTrendsHead = document.getElementById('recent-trends-head');
if (recentTrendsHead && resultEls.trend) wireCollapse(recentTrendsHead, resultEls.trend, 'coach-recent');

// The score-reaction footer (confirm / too harsh / score-a-different-call chips) was removed by request,
// so its click handlers and the in-place "re-score at a lower bar" (reactHarsh) helper went with it.

// --- Quick Help modal (replaces the former always-visible panel; opened from the header button) ---
const quickHelpModal = $('quick-help-modal');
function openQuickHelp(): void {
  quickHelpModal.hidden = false;
}
function closeQuickHelp(): void {
  quickHelpModal.hidden = true;
}
$('quick-help-btn').addEventListener('click', openQuickHelp);
$('quick-help-x').addEventListener('click', closeQuickHelp);
$('quick-help-close').addEventListener('click', closeQuickHelp);
quickHelpModal.addEventListener('click', (e) => {
  if (e.target === quickHelpModal) closeQuickHelp(); // click on the backdrop, not the card
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !quickHelpModal.hidden) closeQuickHelp();
});

// --- "Free call used" modal: shown when a NEW import is blocked by the trial/billing gate (402). Gives
// a clear reason + a path to Billing, instead of the silent bounce-to-Home the gate used to produce. ---
const trialModal = $('trial-modal');
function openTrialModal(code?: string): void {
  if (code === 'BILLING_REQUIRED') {
    $('trial-modal-title').textContent = 'Your billing needs attention';
    $('trial-modal-msg').textContent =
      'To keep scoring calls, please update your payment method or add a plan. Your account, saved reports, and trends stay exactly as they are.';
  } else {
    $('trial-modal-title').textContent = 'Your free trial has ended';
    $('trial-modal-msg').textContent =
      'Your 7 days of free scoring have ended. To keep scoring calls, add a call pack or a plan. Your account, saved reports, and trends stay exactly as they are.';
  }
  trialModal.hidden = false;
}
function closeTrialModal(): void {
  trialModal.hidden = true;
}
$('trial-modal-x').addEventListener('click', closeTrialModal);
$('trial-modal-close').addEventListener('click', closeTrialModal);
$('trial-modal-plans').addEventListener('click', closeTrialModal); // also navigates to Billing via [data-nav-to]
trialModal.addEventListener('click', (e) => {
  if (e.target === trialModal) closeTrialModal(); // backdrop click
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !trialModal.hidden) closeTrialModal();
});
// Seed the import gate on load, and refresh it when the tab regains focus (e.g. returning from the Lemon
// checkout tab after buying calls, which flips the account to active), so the Import button's synchronous
// gate stays accurate without a per-click fetch.
void refreshAccountGate();
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) void refreshAccountGate();
});

// Billing view: "Prefer we set it up? Contact us" navigates to the in-app Support view (Contact us
// section) via the shared [data-nav-to] handler — no separate wiring needed here.

// Self-serve checkout: each buy button mints a Lemon checkout for its variant and redirects there. Until
// checkout is configured the endpoint returns 503 — we show a friendly note and the "contact us"
// fallback stays available.
document.querySelectorAll<HTMLButtonElement>('.bill-buy').forEach((btn) => {
  btn.dataset.label = btn.textContent ?? ''; // remember the resting label (Buy / Subscribe) for reset
  btn.addEventListener('click', () => {
    const variantId = btn.getAttribute('data-variant');
    if (!variantId) return;
    const msg = document.getElementById('billCheckoutMsg');
    const couponEl = document.getElementById('billCoupon') as HTMLInputElement | null;
    const coupon = couponEl?.value.trim() || undefined;
    btn.disabled = true;
    btn.textContent = 'Opening…';
    if (msg) msg.hidden = true;
    void api
      .startCheckout(variantId, coupon)
      .then((r) => {
        window.location.href = r.url; // Lemon Squeezy hosted checkout
      })
      .catch((e: unknown) => {
        btn.disabled = false;
        btn.textContent = btn.dataset.label || 'Buy';
        if (msg) {
          const m = e instanceof Error ? e.message : '';
          msg.textContent = m.includes('not available')
            ? 'Self-serve checkout is being set up. Use “Contact us” below and we’ll sort you out.'
            : "Couldn't start checkout just now. Please try again, or contact Support.";
          msg.hidden = false;
        }
      });
  });
});
// Reset the buy buttons when the user returns via the browser Back button. Redirecting to Lemon leaves a
// button stuck in "Opening…"/disabled; on bfcache restore (or any re-show) we restore the resting label.
window.addEventListener('pageshow', () => {
  document.querySelectorAll<HTMLButtonElement>('.bill-buy').forEach((btn) => {
    btn.disabled = false;
    if (btn.dataset.label) btn.textContent = btn.dataset.label;
  });
  const msg = document.getElementById('billCheckoutMsg');
  if (msg) msg.hidden = true;
});
// Coupon verify: confirm the code against Lemon before checkout and show a green/red result. The verified
// code still rides along to the hosted checkout (startCheckout reads the same field), where Lemon applies +
// enforces it. Applies to any purchase (call credit now; book bundle / subscription on the roadmap).
const couponField = document.getElementById('billCoupon') as HTMLInputElement | null;
const couponApplyBtn = document.getElementById('billCouponApply') as HTMLButtonElement | null;
const couponMsgEl = document.getElementById('billCouponMsg');
function setCouponMsg(text: string, state: 'ok' | 'bad' | 'none'): void {
  if (!couponMsgEl) return;
  couponMsgEl.textContent = text;
  couponMsgEl.classList.remove('is-ok', 'is-bad');
  if (state === 'ok' || state === 'bad') couponMsgEl.classList.add(state === 'ok' ? 'is-ok' : 'is-bad');
  couponMsgEl.hidden = state === 'none' || !text;
}
async function verifyCoupon(): Promise<void> {
  const code = couponField?.value.trim() ?? '';
  if (!code) {
    setCouponMsg('', 'none');
    return;
  }
  if (couponApplyBtn) {
    couponApplyBtn.disabled = true;
    couponApplyBtn.textContent = 'Checking…';
  }
  try {
    const r = await api.validateCoupon(code);
    if (r.valid) setCouponMsg(`Code ${r.code} verified — ${r.label ?? 'discount'} applied at checkout.`, 'ok');
    else setCouponMsg(`We couldn't verify “${code}”. Check the code and try again.`, 'bad');
  } catch {
    setCouponMsg("Couldn't check that code just now. You can still enter it at checkout.", 'bad');
  } finally {
    if (couponApplyBtn) {
      couponApplyBtn.disabled = false;
      couponApplyBtn.textContent = 'Apply';
    }
  }
}
couponApplyBtn?.addEventListener('click', () => void verifyCoupon());
couponField?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    void verifyCoupon();
  }
});
// Editing the code after a result clears the stale confirmation so a verdict never lingers for a different code.
couponField?.addEventListener('input', () => setCouponMsg('', 'none'));
// Prefill a coupon from a shared link (?coupon=CODE or ?discount=CODE) so a campaign / DM link auto-applies
// it. The buyer still sees and can edit the field, and the code also carries through to the hosted checkout.
try {
  const couponParams = new URLSearchParams(window.location.search);
  const preCoupon = (couponParams.get('coupon') || couponParams.get('discount') || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 64);
  const couponInput = document.getElementById('billCoupon') as HTMLInputElement | null;
  if (preCoupon && couponInput) {
    couponInput.value = preCoupon;
    void verifyCoupon(); // a shared link is explicit intent — confirm it right away
  }
} catch {
  /* no query string / no field present — nothing to prefill */
}
async function onDownload(): Promise<void> {
  if (!lastResult) return;
  // Refresh the print sheet from the latest result + Call Details (incl. the Buyer-Signals read, or an
  // upgrade placeholder when the account isn't eligible), then open the print dialog.
  renderReport(reportRoot, lastResult, currentMeta(), getBuyerResult(), lastMeddpicc);
  // Logo/QR images are freshly assigned above — window.print() must wait for them to decode, or
  // print/PDF output can silently omit an image that hasn't finished loading (this is why the QR
  // code previously never appeared: it's the report's only first-use of that asset).
  await waitForReportImages(reportRoot);
  window.print();
  // Lead-gen: email a copy of the report to Project Moneyball (best-effort; never blocks the user).
  try {
    await api.sendReportCopy(lastResult, currentMeta());
  } catch {
    /* mailer not configured / offline — silent by design */
  }
  // Auto-save (Phase 3): archive the report DATA so it can be re-opened later from History. Only when
  // the account has the setting on (the server re-checks too). Best-effort; never affects the download.
  if (autoSaveOn && lastResult) {
    void api.saveReport({ result: lastResult, meta: currentMeta(), buyer: getBuyerResult(), meddpicc: lastMeddpicc }).catch(() => undefined);
  }
}
els.pdfBtn.addEventListener('click', () => void onDownload());

// Email the current report to the signed-in user (Studio Moneyball brand). Present on BOTH the shared
// strip and the Seller Skills strip; feedback goes on whichever button was clicked. Recipient is the
// user's own account email (a "send to a teammate" field comes with the redesign).
async function onEmailReport(btn: HTMLButtonElement): Promise<void> {
  if (!lastResult) return;
  const old = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'SENDING…';
  try {
    await api.emailReport(lastResult, currentMeta());
    btn.textContent = 'SENT ✓';
  } catch {
    btn.textContent = 'FAILED';
  }
  setTimeout(() => {
    btn.textContent = old;
    btn.disabled = false;
  }, 2600);
}
els.emailBtn.addEventListener('click', () => void onEmailReport(els.emailBtn));
skEmailBtn?.addEventListener('click', () => void onEmailReport(skEmailBtn));

// Copy-to-clipboard for the Score Analysis / Recommendations / MEDDPICC boxes. Serializes the
// rendered content to plain text (with the box title) so a manager can paste it into notes/email.
// Serialize the Coaching Summary (tldr + What worked / Areas + Trends + Where-next actions) to plain
// text, in the same top-to-bottom order it renders. Returns '' for the idle/loading scaffold (no
// .coach-tldr yet) so the copy button reports "nothing to copy" instead of grabbing placeholder hints.
function coachingToText(root: HTMLElement): string {
  if (!root.querySelector('.coach-tldr')) return '';
  const lines: string[] = [];
  const walk = (node: Element): void => {
    for (const child of Array.from(node.children)) {
      const c = child.classList;
      if (c.contains('coach-topperf')) {
        const h = child.querySelector('.coach-topperf-h')?.textContent?.trim();
        const t = child.querySelector('.coach-topperf-txt')?.textContent?.trim();
        if (h) lines.push(h.toUpperCase());
        if (t) lines.push(t);
        lines.push('');
      } else if (c.contains('coach-tldr')) {
        const t = child.textContent?.trim();
        if (t) { lines.push(t); lines.push(''); }
      } else if (c.contains('coach-block')) {
        const title = child.querySelector('.coach-block-title')?.textContent?.trim();
        if (title) lines.push(title);
        child.querySelectorAll('.coach-point').forEach((li) => {
          const claim = li.querySelector('.coach-claim')?.textContent?.trim() ?? '';
          const why = li.querySelector('.coach-why')?.textContent?.trim() ?? '';
          lines.push(`• ${`${claim} ${why}`.trim()}`);
        });
        lines.push('');
      } else if (c.contains('coach-subhead')) {
        lines.push((child.textContent?.trim() ?? '').toUpperCase());
      } else if (c.contains('coach-subsubhead')) {
        lines.push('');
        lines.push(child.textContent?.trim() ?? '');
      } else if (c.contains('coach-trend-group')) {
        lines.push(child.textContent?.trim() ?? '');
      } else if (c.contains('coach-trend-row')) {
        const nm = child.querySelector('.coach-trend-name')?.textContent?.trim() ?? '';
        const mv = child.querySelector('.coach-trend-move')?.textContent?.trim() ?? '';
        const lb = child.querySelector('.coach-trend-label')?.textContent?.trim() ?? '';
        lines.push(`• ${`${nm}: ${mv} ${lb}`.trim()}`);
      } else if (c.contains('coach-action')) {
        // First span only — skip the "Open …" nav button label.
        const txt = child.querySelector('span')?.textContent?.trim() ?? '';
        if (txt) lines.push(`• ${txt}`);
      } else if (c.contains('coach-hint')) {
        const t = child.textContent?.trim();
        if (t) lines.push(t);
      } else {
        walk(child); // container (trend wrapper, <ul>) — recurse to preserve order
      }
    }
  };
  walk(root);
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function boxToText(el: HTMLElement): string {
  if (el.id === 'coaching-summary') return coachingToText(el);
  const lines: string[] = [];
  const sections = el.querySelectorAll('.analysis-section');
  const meddItems = el.querySelectorAll('.meddpicc-item');
  if (sections.length) {
    sections.forEach((sec) => {
      const t = sec.querySelector('.analysis-title')?.textContent?.trim();
      if (t) lines.push(t);
      // Cross-Skill Score Analysis: folded per-skill items (name + score + flag, summary, connected note).
      const items = sec.querySelectorAll('.cs-anal-item');
      if (items.length) {
        items.forEach((it) => {
          const nm = it.querySelector('.cs-anal-nm')?.textContent?.trim() ?? '';
          const sc = it.querySelector('.cs-anal-sc')?.textContent?.trim() ?? '';
          const flag = it.querySelector('.cs-anal-flag')?.textContent?.trim() ?? '';
          const why = it.querySelector('.cs-anal-why')?.textContent?.trim();
          const link = it.querySelector('.cs-anal-link')?.textContent?.trim();
          lines.push(`• ${nm} (${sc})${flag ? ` — ${flag}` : ''}`);
          if (why) lines.push(`  ${why}`);
          if (link) lines.push(`  ${link}`);
        });
      } else {
        sec.querySelectorAll('li').forEach((li) => {
          const txt = li.textContent?.trim();
          if (txt) lines.push(`• ${txt}`);
        });
        const empty = sec.querySelector('.analysis-empty')?.textContent?.trim();
        if (empty && !sec.querySelector('li')) lines.push(`• ${empty}`);
      }
      lines.push('');
    });
  } else if (meddItems.length) {
    // MEDDPICC: one block per element — "Letter — Label: Status", then note + evidence.
    meddItems.forEach((it) => {
      const letter = it.querySelector('.meddpicc-letter')?.textContent?.trim() ?? '';
      const label = it.querySelector('.meddpicc-label')?.textContent?.trim() ?? '';
      const status = it.querySelector('.meddpicc-status')?.textContent?.trim() ?? '';
      const note = it.querySelector('.meddpicc-note')?.textContent?.trim();
      const evidence = it.querySelector('.meddpicc-evidence')?.textContent?.trim();
      lines.push(`${letter}, ${label}: ${status}`);
      if (note) lines.push(`  ${note}`);
      if (evidence) lines.push(`  "${evidence}"`);
      lines.push('');
    });
  } else {
    el.querySelectorAll('li').forEach((li) => {
      const txt = li.textContent?.trim();
      if (txt) lines.push(`• ${txt}`);
    });
  }
  return lines.join('\n').trim();
}

function flashCopied(btn: HTMLButtonElement): void {
  const label = btn.querySelector('.copy-label');
  const prev = label?.textContent ?? 'Copy';
  btn.classList.add('copied');
  if (label) label.textContent = 'Copied';
  setTimeout(() => {
    btn.classList.remove('copied');
    if (label) label.textContent = prev;
  }, 1500);
}

async function onCopy(btn: HTMLButtonElement): Promise<void> {
  const targetId = btn.dataset.copy;
  const box = targetId ? document.getElementById(targetId) : null;
  if (!box) return;
  const body = boxToText(box);
  if (!body) {
    notify('warn', 'Nothing to copy yet, run a scoring first.');
    return;
  }
  // Heading for the copied text. Buttons inside a .col-head read the section's own <h3>; a button in
  // the page header (which has no <h3> beside it) names its heading via data-copy-title instead.
  const title =
    btn.closest('.col-head')?.querySelector('.col-h')?.textContent?.trim() || btn.dataset.copyTitle || '';
  try {
    await navigator.clipboard.writeText(title ? `${title}\n\n${body}` : body);
    flashCopied(btn);
  } catch {
    notify('error', 'Copy failed, your browser blocked clipboard access.');
  }
}

document.querySelectorAll<HTMLButtonElement>('.copy-btn').forEach((b) =>
  b.addEventListener('click', () => void onCopy(b)),
);

// Anonymize page: make the "Why use it" / "How to use it" cards collapsible (default collapsed,
// state persisted per device). The header text picks a stable key regardless of block order.
document.querySelectorAll<HTMLElement>('.view[data-view="anonymizer"] .anon-block').forEach((block) => {
  const head = block.querySelector<HTMLElement>('.anon-h');
  const body = block.querySelector<HTMLElement>('.anon-list');
  if (head && body) wireCollapse(head, body, /how/i.test(head.textContent ?? '') ? 'anon-how' : 'anon-why');
});

// Anonymize Transcript page: copy the whole prompt textarea to the clipboard.
const anonCopyBtn = document.getElementById('anon-copy-btn') as HTMLButtonElement | null;
const anonPrompt = document.getElementById('anon-prompt') as HTMLTextAreaElement | null;
if (anonCopyBtn && anonPrompt) {
  const flip = (): void => {
    const prev = anonCopyBtn.textContent ?? 'Copy prompt';
    anonCopyBtn.textContent = 'Copied';
    anonCopyBtn.classList.add('copied');
    setTimeout(() => {
      anonCopyBtn.textContent = prev;
      anonCopyBtn.classList.remove('copied');
    }, 1500);
  };
  // Fallback for browsers/contexts where the async Clipboard API is unavailable (e.g. non-HTTPS):
  // select the textarea and use the legacy execCommand copy.
  const legacyCopy = (): boolean => {
    try {
      anonPrompt.focus();
      anonPrompt.select();
      const ok = document.execCommand('copy');
      anonPrompt.setSelectionRange(0, 0);
      anonPrompt.blur();
      return ok;
    } catch {
      return false;
    }
  };
  anonCopyBtn.addEventListener('click', () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(anonPrompt.value);
        flip();
      } catch {
        if (legacyCopy()) flip();
        else notify('error', 'Copy failed, select the text and copy it manually (Ctrl/Cmd+C).');
      }
    })();
  });
}

// Initial idle state: a blank (from-scratch) scorecard, locked until a transcript is imported and a
// call type identified. Same as the just-cleared state (loadBlank).
loadBlank();
setPhase('idle');
renderCoachingIdle(coachingRoot); // three sub-headers on screen from page load; filled once a call scores

// One-time cleanup: the in-progress diagnostic is now tab-scoped only. Wipe any localStorage copy +
// consent flag left on the device by the retired "keep on this device" opt-in, so nothing lingers.
purgeLegacyDeviceStorage();

// "Coaching signal" banner: MOUNT BEFORE restoreDiagnostic(). A restored diagnostic replays
// applyResult(), which calls showCoachingSignal() — and that starts with `if (!root) return`. Mounted
// after the restore (as it was until 2026-08-23), the call landed before the element existed and was
// silently dropped, so the banner was missing after every hard refresh and only came back on the next
// fresh run. Its CTA opens the guide at "Proving enablement moves the needle".
initCoachingSignal({ openGuide: () => openGuideAt('gs-prove') });
// Subscribe BEFORE wireSideNav()/navigateTo(savedView) below, which fire the view-change events.
// showCoachingSignal() defers the slide-in while the view is still display:none (offsetParent null),
// which is exactly the case during restoreDiagnostic(), so the banner is left PENDING and this
// listener is the only thing that releases it. Registered after those calls (as it was until
// 2026-08-23) the release event had already fired, so a hard refresh landing straight on Quick
// Snapshot left the banner pending forever — it only appeared if the user navigated away and back.
onViewChange(onCoachingSignalView);

// Refresh survival: rebuild the last diagnostic from browser storage. Runs after loadBlank/setPhase
// so it overwrites the idle state rather than being overwritten by it.
restoreDiagnostic();

// Route survival: read the last active view BEFORE wireSideNav (whose initial show() would otherwise
// be the only signal), so a hard refresh returns to the same page instead of Home.
const savedView = loadView();

// Left side-nav: switch between the Home path-picker, the Skill-Based Diagnostic, and the other views.
wireSideNav();

// Restore the last view if it still exists in the DOM. No role re-check needed: the view was in this
// tab's sessionStorage only because the user was already on it this session, so access is implied.
// navigateTo fires onViewChange, so the target view initialises exactly as a click would.
if (savedView && document.querySelector(`.view[data-view="${savedView}"]`)) {
  // Guard: a gated or transiently-broken saved view must NEVER abort the rest of app init below
  // (the account chip, the per-view render hook, etc.). Fall back to the default view on failure.
  try {
    navigateTo(savedView);
  } catch {
    /* stay on the default view */
  }
}
// Returning from a completed Lemon checkout (redirect_url = /app/?billing=success): land on Billing so
// the buyer sees their new balance (navigateTo -> renderBilling refetches), then strip the param so a
// refresh is clean. Best-effort: any failure just leaves the user where they are.
try {
  const sp = new URLSearchParams(window.location.search);
  if (sp.get('billing') === 'success') {
    if (document.querySelector('.view[data-view="billing"]')) navigateTo('billing');
    sp.delete('billing');
    const qs = sp.toString();
    history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  }
} catch {
  /* param handling is best-effort */
}

// Persist future view changes — registered AFTER the restore above so wireSideNav's initial show()
// and the restore itself don't clobber the stored value with the default before we've read it.
onViewChange((v) => saveView(v));

// "Keep the momentum" banner: mount it (first child of .app-region), keep it in sync with the current
// view (stays across group pages, tucks away elsewhere), and dismiss it when the user imports on a group
// page. Currently NOT auto-shown after a score (SHOW_MOMENTUM_BANNER=false, 2026-08-04 batch) — it stays
// mounted/dismissable so it can be resurfaced elsewhere later without rewiring.
const SHOW_MOMENTUM_BANNER = false;
initMomentumBanner({ openImport: () => requestImport() });
onViewChange((v) => onMomentumViewChange(v));
// The "Coaching signal" banner is mounted, and its view listener registered, earlier — before
// restoreDiagnostic() and before the nav fires its first view-change. See there.

// --- Contextual "go back" from Getting Started ---
// When the guide is opened from an in-app CTA we remember the origin view so the rail can offer a way
// back. The action shows ONLY while on the guide after such a CTA; opening the guide from the menu (no
// origin) shows nothing, and leaving the guide clears the context.
let guideReturnView: string | null = null;
const navBackBtn = document.getElementById('nav-back') as HTMLButtonElement | null;
const navBackLabel = document.getElementById('nav-back-label');

function friendlyViewName(view: string): string {
  return document.querySelector(`.nav-item[data-view="${view}"] span`)?.textContent?.trim() || 'the previous page';
}

/** Open the guide from an in-app CTA: remember where we came from, switch to the guide, and scroll to the
 *  section clear of the sticky header (scroll-margin-top handles the offset). */
function openGuideAt(anchorId: string): void {
  const from = document.querySelector('.view.is-active')?.getAttribute('data-view') ?? null;
  guideReturnView = from && from !== 'getting-started' ? from : null;
  navigateTo('getting-started');
  const target = document.getElementById(anchorId);
  if (target) requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
}

function syncNavBack(view: string): void {
  if (view !== 'getting-started') guideReturnView = null; // leaving the guide consumes the context
  const show = view === 'getting-started' && guideReturnView !== null;
  if (navBackBtn) navBackBtn.hidden = !show;
  if (show && navBackLabel && guideReturnView) navBackLabel.textContent = friendlyViewName(guideReturnView);
}
onViewChange(syncNavBack);
navBackBtn?.addEventListener('click', () => {
  const to = guideReturnView;
  guideReturnView = null;
  if (to) navigateTo(to);
});
// Keep the detached Message Center's visibility in sync with the current view (lens + Advanced ON).
onViewChange((v) => {
  callbarMsgView = v;
  syncCallbarMsg();
});
// Re-seed from the CURRENTLY-active view: on a hard refresh the view was already restored by
// navigateTo(savedView) ABOVE, whose onViewChange fired before this handler was subscribed — so without
// this re-read callbarMsgView is stale ('home') and the Message Center stays hidden until the next click.
callbarMsgView = document.querySelector('.view.is-active')?.getAttribute('data-view') ?? callbarMsgView;
syncCallbarMsg();
els.importBtn?.addEventListener('click', () => dismissMomentumBanner()); // #import-btn (Cross-Skill / Buyer-Signals / MEDDPICC)
skImportBtn?.addEventListener('click', () => dismissMomentumBanner()); // #sk-import-btn (Quick Snapshot)
// Account › Preferences "Show Banner" (device pref, default on). The banner's own "Don't show this again"
// flips this off; re-checking here restores the default (banner after every scored call).
const showBannerOptin = document.getElementById('show-banner-optin') as HTMLInputElement | null;
if (showBannerOptin) {
  showBannerOptin.checked = getBannerPref();
  showBannerOptin.addEventListener('change', () => setBannerPref(showBannerOptin.checked));
}

// Account chip (rail foot): avatar + first name + chevron. The chevron opens a popover with Account
// (→ the Account view, where the photo lives) and Sign out. Populated from Clerk, and re-painted when
// the Clerk user changes (e.g. after a photo upload). Replaces the old standalone Sign out button.
// Sign out clears this browser's in-progress diagnostic + last-view so the next sign-in starts fresh,
// then ends the Clerk session (signOutClerk falls back to a plain nav to '/' if Clerk never loaded).
{
  const chip = document.getElementById('user-chip');
  const menu = document.getElementById('user-menu');
  const avatar = document.getElementById('user-avatar');
  const nameEl = document.getElementById('user-chip-name');
  if (chip && menu) {
    const closeMenu = (): void => {
      menu.hidden = true;
      chip.setAttribute('aria-expanded', 'false');
    };
    const openMenu = (): void => {
      menu.hidden = false;
      chip.setAttribute('aria-expanded', 'true');
    };
    chip.addEventListener('click', (e) => {
      e.stopPropagation(); // don't let the document handler below immediately re-close it
      if (menu.hidden) openMenu();
      else closeMenu();
    });
    document.addEventListener('click', (e) => {
      if (!menu.hidden && !menu.contains(e.target as Node) && !chip.contains(e.target as Node)) closeMenu();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !menu.hidden) {
        closeMenu();
        chip.focus();
      }
    });

    document.getElementById('user-menu-account')?.addEventListener('click', () => {
      closeMenu();
      navigateTo('account');
    });
    document.getElementById('user-menu-signout')?.addEventListener('click', () => {
      closeMenu();
      clearDiagnostic(); // both the tab-scoped and any consented device copy
      clearView();
      void signOutClerk();
    });

    onClerkUser((info) => {
      if (avatar) applyAvatar(avatar, info);
      if (nameEl) nameEl.textContent = info?.firstName || 'Account';
      chip.title = info?.fullName || info?.firstName || 'Account';
    });
  }
}
// Support Center: topic picker filters the answer list. Wired synchronously so the filter is live at
// once; the CRO Zone answers/topics stay gated until an admin is confirmed below.
wireSupport();

// Role-based nav. UX only; the worker is the authority. The CRO Zone group is visible to every account
// owner (super_user); only a plain member (lacking its permissions) has it hidden. The paid-plan gate on
// the CRO pages themselves (syncCroGate below) is what asks a free-trial owner to upgrade before use.
void api
  .getMe()
  .then((me) => {
    applyRoleNav(me.permissions);
    if (me.role === 'admin') document.documentElement.classList.add('is-admin');
    // Land the user on their configured Homepage at login. Skip when this tab already restored a saved
    // view (a same-tab refresh keeps you where you were), and only move off the default landing so we
    // never yank the user off a page they opened while /api/me was in flight.
    if (!savedView && me.homepage && me.homepage !== 'home') {
      const active = document.querySelector('.view.is-active')?.getAttribute('data-view');
      if (active === 'home' && document.querySelector(`.view[data-view="${me.homepage}"]`)) {
        try { navigateTo(me.homepage); } catch { /* stay on the default landing */ }
      }
    }
  })
  .catch(() => {});

// Any element with data-nav-to (the Home path-picker cards, the header Support button, inline
// "design a scorecard" links, etc.) switches to the named view when clicked.
for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-nav-to]'))) {
  el.addEventListener('click', (e) => {
    e.preventDefault();
    const v = el.getAttribute('data-nav-to');
    if (v) navigateTo(v);
    // Quick Help links into the app (e.g. "see Support") — navigating behind an open modal would
    // leave the user staring at the modal, so dismiss it.
    closeQuickHelp();
  });
}

// Any element with data-guide-to opens the Getting Started guide scrolled to that section id, and leaves
// a contextual "back" affordance in the rail (openGuideAt). Used by the "explore more" CTAs (e.g. the
// per-skill explainer link on Quick Snapshot).
for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-guide-to]'))) {
  el.addEventListener('click', (e) => {
    e.preventDefault();
    const anchor = el.getAttribute('data-guide-to');
    if (anchor) openGuideAt(anchor);
    closeQuickHelp();
  });
}

// Home Import + Anonymize. The button opens the ONE shared file picker (routes through wireFileImport →
// doImport) and moves the user to Quick Snapshot to watch the score. The checkbox mirrors the Seller
// Skills toolbar Anonymize toggle, so the existing scrub-on-import behavior is unchanged.
const homeImportBtn = document.getElementById('home-import-btn') as HTMLButtonElement | null;
const homeAnonToggle = document.getElementById('home-anon-toggle') as HTMLInputElement | null;
if (homeAnonToggle && anonToggle) homeAnonToggle.checked = anonToggle.checked; // start in sync
homeAnonToggle?.addEventListener('change', () => onAnonChange(homeAnonToggle.checked));
homeImportBtn?.addEventListener('click', () => {
  // Gate BEFORE the picker (and before setting pendingHomeImport): out of calls → upgrade modal, no picker.
  const code = gateBlockCode();
  if (code) {
    openTrialModal(code);
    return;
  }
  if (anonToggle && homeAnonToggle) anonToggle.checked = homeAnonToggle.checked; // synced before import
  // Entering via Import "opens" Quick Snapshot; it opens with Advanced OFF by default (the user flips it
  // on that page if they want the message center + runtime monitor + lifecycle).
  setAdvanced(false);
  // Stay on Home and just open the picker; onFile navigates to Quick Snapshot only once a file is
  // actually chosen (so cancelling the dialog leaves the user on Home).
  pendingHomeImport = true;
  els.fileInput.click(); // native picker → onFile → (navigate) → doImport
});

// "Paste your script" → reveal a textarea and route the pasted text through the SAME import/score flow as
// a file. The transcript is server-side Input-Guarded, Anonymized (when the toggle is on), and Output-
// Gated exactly like a file import — nothing new reaches the model unguarded.
const homePasteBtn = document.getElementById('home-paste-btn') as HTMLButtonElement | null;
const homePasteBox = document.getElementById('home-paste-box');
const homePasteInput = document.getElementById('home-paste-input') as HTMLTextAreaElement | null;
const homePasteScore = document.getElementById('home-paste-score') as HTMLButtonElement | null;
const homePasteHint = document.getElementById('home-paste-hint');
const PASTE_MAX = 200_000; // matches the server Input Guard cap (beyond this the guard truncates)
homePasteBtn?.addEventListener('click', () => {
  if (homePasteBox) homePasteBox.hidden = false;
  homePasteInput?.focus();
});
homePasteInput?.addEventListener('input', () => {
  if (!homePasteHint || !homePasteInput) return;
  const n = homePasteInput.value.length;
  homePasteHint.textContent = !n
    ? ''
    : n > PASTE_MAX
      ? `${n.toLocaleString()} characters — only the first ${PASTE_MAX.toLocaleString()} will be scored`
      : `${n.toLocaleString()} characters`;
});
homePasteScore?.addEventListener('click', () => {
  const text = (homePasteInput?.value ?? '').slice(0, PASTE_MAX);
  if (!text.trim()) {
    notify('warn', 'Paste a transcript first.');
    homePasteInput?.focus();
    return;
  }
  // Gate before spending a call, same as the Import button.
  const code = gateBlockCode();
  if (code) {
    openTrialModal(code);
    return;
  }
  if (anonToggle && homeAnonToggle) anonToggle.checked = homeAnonToggle.checked; // sync Anonymize before import
  setAdvanced(false);
  rawTranscript = text;
  transcript = text;
  importedFileName = 'Pasted transcript';
  autoRunOnImport = true;
  if (homePasteBox) homePasteBox.hidden = true;
  if (homePasteInput) homePasteInput.value = '';
  if (homePasteHint) homePasteHint.textContent = '';
  navigateTo('home');
  enterLabWithTransition(() => void doImport('Pasted transcript'));
});

// Home "recommended steps" cards: each button deep-links to its page via the same nav router the
// sidebar uses. data-nav carries the target view id (teams / scorecards / account / getting-started).
for (const item of document.querySelectorAll<HTMLElement>('.home-guide-item[data-nav]')) {
  item.addEventListener('click', () => {
    const view = item.getAttribute('data-nav');
    if (view) navigateTo(view);
  });
}

// (The CRO "benchmark" audience line and its deep-link moved to the marketing landing page.)

// Scorecards page: per-call-type manager scorecards. onSave (Diagnostic) calls scorecards.refresh()
// so a save here is reflected on that page's Custom column without a reload.
const scorecards = new ScorecardsPage();
void scorecards.init();

// Secret Sauce page (automatic Top-vs-Core differentiator engine). Rendered on view-open by
// renderForView; the manual cohort-upload flow now lives on the Compare page ("upload two sets").

// Buyer-Side Diagnostic page. Standalone mode; reuses the currently-imported transcript by default
// (guideline 6) and refreshes its reuse notice whenever the view is shown.
const buyerSide = new BuyerSidePage(
  () => (transcript.trim() ? { text: transcript } : null),
  () => requestImport(), // import routes through the ONE shared (gated) flow, not a private copy
  () => persistNow(), // an auto-run's result is folded into the snapshot so a refresh restores it
  () => callBar?.refresh(), // run start/stop → refresh the call-bar so its Stop action appears/hides
);
getBuyerResult = () => buyerSide.getResult(); // now safe for persistNow to read the buyer result
// Re-render a Buyer-Signals result carried over from a restored snapshot (auto-run has no re-run button).
if (restoredBuyerResult) {
  buyerSide.restore(restoredBuyerResult);
  // restore's own applyResult() persisted buyerSide:null (buyer wasn't wired yet) — reconcile the
  // snapshot now that the buyer read is back on screen, so a second refresh keeps it too.
  persistNow();
}
// Per-view data render, run each time a view opens (and once for the hard-refresh restore below).
function renderForView(v: string): void {
  if (v === 'buyer-side') buyerSide.onShow();
  if (v === 'account') renderAccount(); // refresh the self-service account overview each time it opens
  if (v === 'billing') void renderBilling(); // plan summary + invoice history from Lemon, each time it opens
  if (v === 'trends') renderTrendsPage(); // CRO Zone: per-skill trend charts (filters in the right rail)
  if (v === 'heatmap') renderHeatmapPage(); // CRO Zone: the skill×week heatmap (shares the Trends filters)
  if (v === 'team-trends') renderTeamTrends(); // manager 24-month score history per rep
  if (v === 'teams') renderTeamsPage(); // create/rename/delete teams + assign reps
  if (v === 'history') renderHistory(); // auto-saved report archive
  if (v === 'compare') renderCompare(); // 2-rep / 2-team per-skill comparison
  if (v === 'bespoke-playbook') void renderSecretSauce(); // Secret Sauce: automatic Top-vs-Core differentiators
}
onViewChange(renderForView);
// Hard-refresh restore fires navigateTo() far earlier (near wireSideNav), BEFORE this render hook is
// registered, so a refresh landing on Account/Billing/Team pages would show an empty panel until the
// user re-clicked it. Now that the hook exists, render the restored view once.
if (savedView && document.querySelector(`.view[data-view="${savedView}"]`)) renderForView(savedView);

// History → re-render a saved report from its stored data and re-open the print dialog (same PDF).
async function openSavedReport(id: string): Promise<void> {
  try {
    const data = await api.getReport(id);
    if (!data?.result) return;
    renderReport(reportRoot, data.result as AggregateResult, (data.meta as ReportMeta) ?? currentMeta(), (data.buyer as BuyerSideResult) ?? null, (data.meddpicc as MeddpiccReport) ?? null);
    await waitForReportImages(reportRoot);
    window.print();
  } catch {
    /* best-effort — a missing/expired object just does nothing */
  }
}
setReportOpener((id) => void openSavedReport(id));

// Shared Call context bar + lens tabs — the "import once, no re-import" band across the three
// single-call lenses. Reads the current call (session/transcript); Replace reuses the import file
// picker, Clear reuses onClear. It refreshes from setPhase and on every view change (mountCallBar).
callBar = mountCallBar({
  container: $('call-bar'),
  getState: () => ({ session, hasContent, busy: currentPhase === 'running' || currentPhase === 'importing', scored: currentPhase === 'done', buyerRunning: buyerSide.isRunning(), fileName: importedFileName }),
  // Give the lens rail the SAME interactive "Taken / Set Timestamp" control as Quick Snapshot, reading
  // and writing the one shared session.call_meta — so setting the timestamp on either page shows on both.
  onDetailsRendered: (el) => appendCallDate(el, currentPhase),
});
callBar.refresh();

// Mirror the Quick Snapshot score into the shared lens rail's score card, so a dark-skinned lens page
// shows the same call's score beside its own content. Read-only copy of the #score-circle + meaning /
// coverage / bonus; hidden until a score exists.
(() => {
  const card = document.getElementById('cb-score-card');
  const circle = document.getElementById('cb-score-circle');
  const meaning = document.getElementById('cb-score-meaning');
  const coverage = document.getElementById('cb-score-coverage');
  const bonus = document.getElementById('cb-score-bonus');
  const benchOpt = document.getElementById('benchmark-opt');
  const benchToggle = document.getElementById('benchmark-toggle') as HTMLInputElement | null;
  const cbBenchOpt = document.getElementById('cb-benchmark-opt');
  const cbBenchToggle = document.getElementById('cb-benchmark-toggle') as HTMLInputElement | null;
  if (!card || !circle) return;
  const sync = (): void => {
    circle.className = resultEls.circle.className;
    circle.textContent = resultEls.circle.textContent;
    if (meaning) meaning.textContent = resultEls.meaning.textContent;
    if (coverage) coverage.textContent = resultEls.coverage.textContent;
    if (bonus && resultEls.bonus) bonus.textContent = resultEls.bonus.textContent;
    if (cbBenchOpt && benchOpt) cbBenchOpt.hidden = benchOpt.hidden;
    if (cbBenchToggle && benchToggle) cbBenchToggle.checked = benchToggle.checked;
    card.hidden = !((resultEls.circle.textContent || '').trim());
  };
  const obs = new MutationObserver(sync);
  [resultEls.circle, resultEls.meaning, resultEls.coverage, resultEls.bonus, benchOpt].forEach((el) => {
    if (el) obs.observe(el, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden'] });
  });
  // Two-way "Add this call to Benchmark": either checkbox drives the same underlying state.
  benchToggle?.addEventListener('change', () => { if (cbBenchToggle) cbBenchToggle.checked = benchToggle.checked; });
  cbBenchToggle?.addEventListener('change', () => {
    if (benchToggle && benchToggle.checked !== cbBenchToggle.checked) {
      benchToggle.checked = cbBenchToggle.checked;
      benchToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  // Match Quick Snapshot's (i) tooltips on the lens score card, sourced from the SAME copy (bonus from
  // bonusCopy, benchmark cloned from the QS control) so the text can never diverge.
  const cbBonusLabel = document.getElementById('cb-score-bonus')?.closest('.cb-stat')?.querySelector('.score-coverage-label');
  if (cbBonusLabel && !cbBonusLabel.querySelector('.meta-info')) cbBonusLabel.appendChild(bonusInfoIcon());
  const qsBenchInfo = benchOpt?.querySelector('.meta-info');
  if (cbBenchOpt && qsBenchInfo && !cbBenchOpt.querySelector('.meta-info')) cbBenchOpt.appendChild(qsBenchInfo.cloneNode(true));
  // Score-legend (i): the QS verdict band is hidden here, so clone its legend into this card too.
  // Placed at the TOP of the verdict column (next to the score, above "Meaning:") rather than the
  // card corner, so it reads with the score and clears the cockpit corner screw.
  const qsLegendInfo = document.querySelector('.verdict-band .score-legend-info');
  const cbVerdict = card.querySelector('.cb-verdict');
  if (qsLegendInfo && cbVerdict && !card.querySelector('.score-legend-info')) {
    cbVerdict.insertBefore(qsLegendInfo.cloneNode(true), cbVerdict.firstChild);
  }
  sync();
})();

